/**
 * @file Canonical agent contract types: request/response shapes for the
 * Cloud Run endpoint, the agentic SSE event stream, model parameters, tool
 * definitions (with `scopes`, `requires`, `requiresToggle`, `pdfExtractionPath`
 * and `requiresApproval`), approval prompts, and the email-extraction config
 * the analysis pipeline persists. The read-only ledger tools reach the ledger
 * only through its callables, whose rights check is authoritative. Shared verbatim between the web app
 * and Cloud Run so a single change updates both ends of the wire.
 */
import type { Timestamp } from 'firebase/firestore';

import type { DocId, DocDataWithId, Fields, Meta, FormVersion, EntityReferences, FormTypeId, RowClassificationTag } from './System.types';
import type { FormStateId } from './Client.Parameters.types';
import type { TypeId, EntityId, ArTableId } from './System.Artifacts.types';
import type { Workflow, WorkflowSummary } from './System.Workflow.types';
import { workflowDataJsonSchema } from './System.WorkflowJsonSchema.types';
import type { FormLevelFieldDescriptors } from './System.FormLevelFieldDescriptors.types';
import type { UserRightId } from './AccessControl.types';
import type { AgentClientContext, AgentToolScope, ApprovalReason, ApprovalSpec, ToggleKey } from './AgentContext.types';
import { MAX_COMMUNICATION_LOG_BODY_SIZE } from './System.Parameters.types';
import type { PdfExtractionPath } from './System.Parameters.types';
import { LEDGER_EXCEPTION_KINDS } from './System.Ledger.types';
import { buildAgentSystemPrompt, systemAgentUserPrompt } from './System.AgentPrompts.types';
import { ANALYTICS_SCOPE_LEVELS, NAMED_QUERY_IDS, NAMED_QUERY_PARAMETER_ENUMS, NAMED_QUERY_SERIES_PARAMETER_ENUMS } from './Analytics.NamedQuery.types';
import type { AnalyticsQueryProvenance } from './Analytics.NamedQuery.types';

/**
 * Thinking mode for the agent model
 */
export type AgentThinkingMode = 'standard' | 'thinking';

/**
 * Reasoning effort level for the agent model
 */
export type AgentReasoningEffort = 'low' | 'medium' | 'high';

/**
 * Per-part media resolution for an image sent to a Gemini 3 model. Mirrors the
 * `PartMediaResolutionLevel` values `@google/genai` accepts, minus
 * `UNSPECIFIED`; other providers ignore it.
 */
export type AgentMediaResolution = 'low' | 'medium' | 'high' | 'ultra_high';

/**
 * Thinking level for a Gemini 3 model. Mirrors the `ThinkingLevel` values
 * `@google/genai` accepts, minus `UNSPECIFIED`; other providers ignore it.
 */
export type AgentThinkingLevel = 'minimal' | 'low' | 'medium' | 'high';

/**
 * Parameters for configuring the agent language model
 */
export type AgentModelParams = {
  /** System prompt for the model */
  system: string;
  /** Model identifier (e.g., 'claude-sonnet-4-6', 'gpt-5.3-chat-latest', 'gemini-2.5-flash') */
  model: string;
  /** Maximum number of tokens to generate */
  maxTokens: number;
  /** Temperature setting (0-2), controls randomness */
  temperature: number;
  /** Top-p sampling parameter (0-1), controls diversity */
  topP: number;
  /** Optional thinking mode */
  thinkingMode?: AgentThinkingMode;
  /** Optional reasoning effort level */
  reasoningEffort?: AgentReasoningEffort;
};

/**
 * Available thinking mode options for UI selection
 */
export const agentThinkingModeOptions = [
  { value: 'standard', label: 'Standard' },
  { value: 'thinking', label: 'Thinking' },
] as const;

/**
 * Available reasoning effort options for UI selection
 */
export const agentReasoningEffortOptions = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
] as const;

/**
 * Provider identifier. Each provider exposes a subset of the call shapes
 * declared in {@link ProviderKind}.
 */
export type ProviderId = 'anthropic' | 'openai' | 'gemini' | 'typesafe';

/**
 * Call-shape discriminator. Names match both the registry tag and the method
 * called on the provider — one decision point everywhere:
 *  - `'unstructured'` — text in, text out (streaming). Method: `unstructured()`.
 *  - `'structured-output'` — text in, typed JSON out. Method: `structuredOutput()`.
 *  - `'structured'` — typed in, typed out (Typesafe). Method: `structured()`.
 *    Sub-discriminants live on the prompt: `noul | choice | score`.
 */
export type ProviderKind = 'unstructured' | 'structured-output' | 'structured';

/**
 * Enumeration of every {@link ProviderKind} value. `satisfies` pins the tuple
 * to the union — adding a kind without updating this constant fails compilation.
 * Used where iteration over the discriminant is unavoidable (e.g. assembling
 * a "supported kinds" diagnostic string).
 */
export const PROVIDER_KINDS = ['unstructured', 'structured-output', 'structured'] as const satisfies readonly ProviderKind[];

/**
 * Token usage reported by a provider on a completed call. Shared by every AI
 * component so metering, telemetry, and streaming all read one shape.
 *
 * `cacheCreationTokens` / `cacheReadTokens` are populated by providers with
 * prompt caching — Anthropic (both fields) and Gemini (`cacheReadTokens` only,
 * from implicit context caching) — and, when set, EXCLUDE those cached tokens
 * from `inputTokens`. OpenAI folds cached tokens into its input count and
 * leaves the cache fields unset.
 *
 * `outputTokens` always includes any reasoning tokens, so it is the billable
 * output. `thinkingTokens` is the informational subset of `outputTokens` spent
 * on reasoning, set by providers that report it (Gemini `thoughtsTokenCount`);
 * never add it to `outputTokens` again.
 */
export type ProviderUsage = {
  inputTokens: number;
  outputTokens: number;
  thinkingTokens?: number;
  cacheCreationTokens?: number;
  cacheReadTokens?: number;
};

/**
 * Declares which sampling/reasoning controls a model accepts. Only meaningful
 * for providers that support `'unstructured'` or `'structured-output'` calls.
 * Providers read these flags to build API requests — no model-name branching needed.
 */
export type LlmCapabilities = {
  /** Controls which sampling parameters the model accepts:
   *  - `'both'`  — accepts temperature AND top-p independently (OpenAI, Gemini)
   *  - `'either'` — accepts temperature OR top-p, but not both in one request (Anthropic)
   *  - `'none'`  — no sampling control; uses reasoning effort instead (e.g. gpt-5-mini)
   */
  sampling: 'both' | 'either' | 'none';
  /** Model supports effort-level control (Anthropic output_config.effort / OpenAI reasoning.effort) */
  effort: boolean;
  /** Model supports extended thinking with a token budget (Anthropic-style) */
  thinking: boolean;
  /** Thinking cannot be turned off (Gemini Pro family): standard mode falls back to a dynamic budget instead of disabling it. Ignored when `thinking` is false. */
  thinkingAlwaysOn?: boolean;
  /** Model accepts image inputs natively (vision) */
  vision: boolean;
  /**
   * Gemini 3 `thinkingConfig.thinkingLevel` values the model accepts, lowest
   * first, as the Gemini docs list them per model. Absent when the model takes
   * no thinking level (Gemini 2.5 takes only a thinking budget).
   */
  thinkingLevels?: ReadonlyArray<AgentThinkingLevel>;
  /** Model accepts Gemini 3 per-part `mediaResolution` on image parts. */
  mediaResolution?: boolean;
};

export type LlmModel = {
  label: string;
  provider: ProviderId;
  /** Sampling / reasoning controls. Absent for `'structured'`-only providers. */
  capabilities?: LlmCapabilities;
};

/**
 * Single source of truth for every known LLM: provider + capabilities.
 */
export const LLM_MODELS: Record<string, LlmModel> = {
  'claude-sonnet-5': {
    label: 'Claude Sonnet 5',
    provider: 'anthropic',
    capabilities: { sampling: 'none', effort: true, thinking: true, vision: true },
  },
  'claude-sonnet-4-6': {
    label: 'Claude Sonnet 4.6',
    provider: 'anthropic',
    capabilities: { sampling: 'either', effort: true, thinking: true, vision: true },
  },
  'claude-haiku-4-5': {
    label: 'Claude Haiku 4.5',
    provider: 'anthropic',
    capabilities: { sampling: 'either', effort: false, thinking: true, vision: true },
  },
  'claude-opus-4-6': {
    label: 'Claude Opus 4.6',
    provider: 'anthropic',
    capabilities: { sampling: 'either', effort: true, thinking: true, vision: true },
  },
  'gpt-5.3-chat-latest': {
    label: 'GPT 5.3',
    provider: 'openai',
    capabilities: { sampling: 'both', effort: true, thinking: false, vision: true },
  },
  'gpt-5-mini': {
    label: 'GPT 5 Mini',
    provider: 'openai',
    capabilities: { sampling: 'none', effort: true, thinking: false, vision: true },
  },
  'gpt-5.4': {
    label: 'GPT 5.4',
    provider: 'openai',
    capabilities: { sampling: 'both', effort: true, thinking: false, vision: true },
  },
  'gpt-5.4-mini': {
    label: 'GPT 5.4 Mini',
    provider: 'openai',
    capabilities: { sampling: 'none', effort: true, thinking: false, vision: true },
  },
  'gpt-5.4-nano': {
    label: 'GPT 5.4 Nano',
    provider: 'openai',
    capabilities: { sampling: 'none', effort: true, thinking: false, vision: true },
  },
  'gemini-flash-lite-latest': {
    label: 'Gemini Flash-Lite (latest)',
    provider: 'gemini',
    capabilities: { sampling: 'both', effort: false, thinking: true, vision: true },
  },
  'gemini-flash-latest': {
    label: 'Gemini Flash (latest)',
    provider: 'gemini',
    capabilities: { sampling: 'both', effort: false, thinking: true, vision: true },
  },
  'gemini-pro-latest': {
    label: 'Gemini Pro (latest)',
    provider: 'gemini',
    capabilities: { sampling: 'both', effort: false, thinking: true, thinkingAlwaysOn: true, vision: true },
  },
  'gemini-3.5-flash': {
    label: 'Gemini 3.5 Flash',
    provider: 'gemini',
    capabilities: { sampling: 'both', effort: false, thinking: true, vision: true, thinkingLevels: ['minimal', 'low', 'medium', 'high'], mediaResolution: true },
  },
  'gemini-3.8-flash': {
    label: 'Gemini 3.8 Flash',
    provider: 'gemini',
    capabilities: { sampling: 'both', effort: false, thinking: true, vision: true, thinkingLevels: ['low', 'medium', 'high'], mediaResolution: true },
  },
  'gemini-3.1-pro-preview': {
    label: 'Gemini 3.1 Pro',
    provider: 'gemini',
    capabilities: { sampling: 'both', effort: false, thinking: true, thinkingAlwaysOn: true, vision: true, thinkingLevels: ['low', 'medium', 'high'], mediaResolution: true },
  },
  'gemini-3.1-flash-lite': {
    label: 'Gemini 3.1 Flash-Lite',
    provider: 'gemini',
    capabilities: { sampling: 'both', effort: false, thinking: true, vision: true, thinkingLevels: ['minimal', 'low', 'medium', 'high'], mediaResolution: true },
  },
  'gemini-2.5-flash': {
    label: 'Gemini 2.5 Flash',
    provider: 'gemini',
    capabilities: { sampling: 'both', effort: false, thinking: true, vision: true },
  },
  'gemini-2.5-pro': {
    label: 'Gemini 2.5 Pro',
    provider: 'gemini',
    capabilities: { sampling: 'both', effort: false, thinking: true, thinkingAlwaysOn: true, vision: true },
  },
  'speed_latest': {
    label: 'Typesafe Speed',
    provider: 'typesafe',
  },
};

/** Native unit a model is metered in for credit accounting. */
export type CreditUnit = 'tokens' | 'calls' | 'pages';

/** The native metering unit and the credit weight charged per native unit. */
export interface CreditRate {
  unit: CreditUnit;
  weight: number;
}

/**
 * Credit rate per model-id prefix, consumed server-side by `creditRate()` in the
 * metering layer. The LONGEST matching prefix wins, so a specific tier
 * (`claude-opus`, `gemini-2.5-flash`) beats its family default (`claude-`,
 * `gemini-`) while every dated or suffixed variant still resolves (e.g.
 * `claude-haiku-4-5-20251001`, `gemini-flash-lite-latest`) without being
 * enumerated.
 *
 * ⚠️ Weights are illustrative placeholders on a scale of 1 credit = USD 0.0001
 * (10,000 credits = $1): token weights are per-token, Typesafe is per-call,
 * Landing and Document AI are per-page. Set them from real provider list prices
 * before launch. `gcp_document_ai_ocr` (the Enterprise Document OCR processor,
 * $1.50 per 1,000 pages) is priced at 15% of `gcp_document_ai` (the Layout
 * Parser, $10 per 1,000 pages), the ratio of their list prices.
 */
export const MODEL_RATES: ReadonlyArray<{ prefix: string } & CreditRate> = [
  { prefix: 'claude-opus',       unit: 'tokens', weight: 0.20 },
  { prefix: 'claude-haiku',      unit: 'tokens', weight: 0.015 },
  { prefix: 'claude-',           unit: 'tokens', weight: 0.06 },
  { prefix: 'gpt-5.4-nano',      unit: 'tokens', weight: 0.004 },
  { prefix: 'gpt-5.4-mini',      unit: 'tokens', weight: 0.008 },
  { prefix: 'gpt-5-mini',        unit: 'tokens', weight: 0.008 },
  { prefix: 'gpt-',              unit: 'tokens', weight: 0.04 },
  { prefix: 'o1',                unit: 'tokens', weight: 0.04 },
  { prefix: 'o3',                unit: 'tokens', weight: 0.04 },
  { prefix: 'o4',                unit: 'tokens', weight: 0.04 },
  { prefix: 'gemini-3.8-flash',  unit: 'tokens', weight: 0.015 },
  { prefix: 'gemini-2.5-pro',    unit: 'tokens', weight: 0.04 },
  { prefix: 'gemini-2.5-flash',  unit: 'tokens', weight: 0.006 },
  { prefix: 'gemini-flash-lite', unit: 'tokens', weight: 0.004 },
  { prefix: 'gemini-',           unit: 'tokens', weight: 0.02 },
  { prefix: 'speed_',            unit: 'calls',  weight: 20 },
  { prefix: 'typesafe',          unit: 'calls',  weight: 20 },
  { prefix: 'gcp_document_ai_ocr', unit: 'pages', weight: 15 },
  { prefix: 'gcp_document_ai',   unit: 'pages',  weight: 100 },
  { prefix: 'landing',           unit: 'pages',  weight: 50 },
];

/**
 * Model used for column-mapping inference (source columns → target schema).
 * Pinned to a single model so mappings are reproducible across deploys.
 */
export const MAPPING_MODEL = 'claude-sonnet-4-6' as const;

/**
 * Default model for inbound email analysis (intent extraction, structured
 * output, attachment parsing). Overridable per-tenant via
 * `AiEmailAnalysisParameters.model`.
 */
export const defaultEmailAnalysisModel = 'claude-sonnet-4-6' as const;

/**
 * Default configuration for language model requests.
 * Provides sensible defaults for common use cases.
 */
export const defaultModelParams = {
  system: buildAgentSystemPrompt(systemAgentUserPrompt),
  model: 'gemini-flash-lite-latest',
  maxTokens: 8000,
  temperature: 0.5,
  topP: 0.1,
  thinkingMode: 'thinking',
  reasoningEffort: 'low',
} as const satisfies AgentModelParams;

/**
 * Infer the provider backend from a model identifier.
 * Looks up LLM_MODELS first, then falls back to prefix matching.
 */
export const providerFromModel = (model: string): ProviderId => {
  const entry = LLM_MODELS[model];
  if (entry) return entry.provider;
  if (model.startsWith('claude-')) return 'anthropic';
  if (model.startsWith('gpt-') || model.startsWith('o1') || model.startsWith('o3') || model.startsWith('o4')) return 'openai';
  if (model.startsWith('gemini-')) return 'gemini';
  if (model.startsWith('speed_')) return 'typesafe';
  throw new Error(`Unknown model: "${model}" — no provider matches`);
};

/**
 * Whether a model supports a given call shape. Branches on the provider
 * discriminant — exhaustiveness pinned by the switch (no `default`).
 */
export const supportsKind = (model: string, kind: ProviderKind): boolean => {
  const id = providerFromModel(model);
  switch (id) {
    case 'openai':
    case 'anthropic':
    case 'gemini':
      return kind === 'unstructured' || kind === 'structured-output';
    case 'typesafe':
      return kind === 'structured';
  }
};

/**
 * UI select options for chat / agent pickers. Filters to models that support
 * `'unstructured'` (streaming text), since chat needs streaming.
 */
export const unstructuredModelOptions = Object.entries(LLM_MODELS)
  .filter(([id]) => supportsKind(id, 'unstructured'))
  .map(([value, { label }]) => ({ value, label }));

/**
 * UI select options for typed-IO model pickers (Typesafe). Filters to models
 * that support `'structured'` calls.
 */
export const structuredModelOptions = Object.entries(LLM_MODELS)
  .filter(([id]) => supportsKind(id, 'structured'))
  .map(([value, { label }]) => ({ value, label }));

/**
 * Look up an LLM model's declared capabilities by its identifier string.
 * Returns sensible defaults for unknown models. Models without text-LLM
 * capabilities (e.g. Typesafe) get the same defaults — callers shouldn't ask.
 */
export const modelCapabilities = (model: string): LlmCapabilities =>
  LLM_MODELS[model]?.capabilities ?? { sampling: 'both', effort: false, thinking: false, vision: false };

/**
 * Types of tasks that can be performed by the language model
 */
export type LLMTask =
  | 'updateFormVersion'
  | 'listWorkflows'
  | 'getWorkflow'
  | 'createWorkflow'
  | 'updateWorkflow'
  | 'deleteWorkflow'
  | 'savePdfIntakeDecisions'
  | 'approvePdfIntakeSchedule'
  | ''
  | null;

/**
 * Structure definition for expected output format
 */
export interface OutputStructure {
    /** The expected type or format of the output */
    type: string;
    /** JSON Schema describing the expected output structure */
    schema: Record<string, unknown>;
}

/**
 * A single prior conversation turn sent as context.
 * Slim, JSON-safe shape; backend-only fields like tool calls are not carried.
 */
export interface ChatHistoryMessage {
    role: 'user' | 'assistant';
    content: string;
}

/**
 * Reference to an image stored in Firebase Storage that the model should view
 * directly as a vision content block (no extractFromFile round-trip).
 */
export interface AgentImageRef {
    /** Firebase Storage path under the user's temp inventory (`temp/<uid>/...`). */
    storagePath: string;
    /** IANA media type, e.g. `image/png`. */
    mediaType: string;
}

/**
 * Request interface for language model operations
 */
export interface LanguageModelRequest {
    /** The input prompt for the language model */
    prompt: string;
    /** Optional task type for the language model */
    taskType?: LLMTask;
    /** Optional structure definition for the expected output */
    outputStructure?: OutputStructure;
    /** Optional model parameters override */
    modelParams?: Partial<AgentModelParams>;
    /** Optional client-shipped context (page descriptor + toggles). Cloud
     *  attaches `user` and `schema` to produce the resolved `AgentContext`. */
    context?: AgentClientContext;
    /** Optional agentic mode configuration */
    agenticMode?: AgenticModeConfig;
    /** Optional tool names to resolve from the registry (defaults to standard set) */
    tools?: AgentToolName[];
    /** Prior conversation turns to send as context, oldest first. Excludes the current prompt. */
    history?: ChatHistoryMessage[];
    /** Images to inline as vision blocks on the first user message. */
    imageRefs?: AgentImageRef[];
    /** Set by the React side when the user navigated away from the bound page
     *  while the previous loop was running. The next tool result the model
     *  sees carries the flag; the agent decides whether to `finish`. */
    userNavigatedAway?: boolean;
    /** Stable per-conversation id minted on the agentChat doc; emitted as `$ai_session_id` so a multi-interaction import's traces group in PostHog. */
    sessionId?: string;
}

/**
 * Task interface for language model operations with specific targets and updates
 */
export interface LanguageModelTask {
    /** Type of task to perform */
    taskType: LLMTask;
    
    /** Target document identifiers */
    target?: {
        /** Form document ID */
        formId?: string;
        /** Form version document ID */
        formVersionId?: string;
    };

    /** Updates to apply */
    updates?: {
        /** Field updates */
        fields?: Fields;
        /** Entity reference updates */
        entityReferences?: EntityReferences;
    };

    /** Standard metadata */
    meta?: Meta;

    /** Optional model parameters override */
    modelParams?: Partial<AgentModelParams>;
}

/**
 * Result interface for language model task operations
 */
export interface LanguageModelTaskResult {
    /** Whether the task completed successfully */
    success: boolean;
    /** Error message if task failed */
    error?: string;
    /** Task result data */
    result?: {
        /** Updated form version if applicable */
        formVersion?: FormVersion;
        /** Additional result data */
        [key: string]: any;
    };
}

/**
 * Tool parameter definition for agent tools
 */
export interface AgentToolParameter {
  /** Parameter name */
  name: string;
  /** Parameter type (string, number, boolean, object, array) */
  type: 'string' | 'number' | 'boolean' | 'object' | 'array';
  /** Parameter description */
  description: string;
  /** Whether the parameter is required */
  required?: boolean;
  /** Item type for array parameters */
  items?: { type: 'string' | 'number' | 'boolean' | 'object' };
  /** Full JSON Schema for this parameter. When present, providers use it verbatim instead of the flat `{ type, description, items }` shape. */
  schema?: Record<string, unknown>;
}

/**
 * Tool definition for agent tools
 */
export interface AgentToolDefinition {
  /** Unique tool name */
  name: string;
  /** User-facing label shown in the chat UI. */
  displayName: string;
  /** Tool description for the model */
  description: string;
  /** Tool parameters */
  parameters: AgentToolParameter[];
  /** Page scopes in which this tool appears. The resolver unions the slices matching the active page. */
  scopes: AgentToolScope[];
  /** Optional OR-set of user rights; the resolver drops the tool when the user holds none of them. */
  requires?: UserRightId[];
  /** Optional OR-set of session toggles; the resolver drops the tool when none are enabled. */
  requiresToggle?: ToggleKey[];
  /** Offered only while `PDF_EXTRACTION_PATH` equals this; absent means offered on either path. */
  pdfExtractionPath?: PdfExtractionPath;
  /** Per-call user approval gate — static reasons or a function deciding per-input. */
  requiresApproval?: ApprovalSpec;
  /** Cheap server-side preview used to render the approval dialog. Tools with `requiresApproval` must implement this. */
  preview?: (input: unknown) => { summary: string; counts?: Record<string, number> };
  /** `false` closes the input schema: an argument the parameters do not name is refused by the provider. */
  additionalProperties?: false;
}

export const AI_AGENT_RIGHTS: readonly UserRightId[] = ['aiAgent', 'aiOperator', 'aiAdmin'];

// ─── Workflow Action Tools ───────────────────────────────────────────────────

export const duplicateRowsTool: AgentToolDefinition = {
    name: 'duplicateRows',
    displayName: 'Copy table rows',
    description: 'Duplicate rows within a table on the form version or record bound to the current page. Use scope "selectedRows" with selectedRowIds, or "allRows". `cellOverrides` overwrite values on the new copies.',
    parameters: [
        { name: 'tableId', type: 'string', description: 'The table type ID to duplicate rows in', required: true },
        { name: 'scope', type: 'string', description: '"selectedRows" or "allRows"', required: true },
        { name: 'selectedRowIds', type: 'array', items: { type: 'string' }, description: 'Row IDs to duplicate (required when scope is "selectedRows")', required: false },
        { name: 'cellOverrides', type: 'array', items: { type: 'object' }, description: 'Optional array of { columnId, value } overrides for the duplicated rows. columnId is a plain column id, or "parent.sub" for one sub-field of a composite column; no slashes.', required: false },
    ],
    scopes: ['formVersion', 'record'],
    requires: ['formsWrite', 'recordsWrite', 'formsAdmin', 'recordsAdmin', 'orgsAdmin'],
    requiresApproval: (input: unknown) => {
        const obj = (input ?? {}) as { scope?: unknown; selectedRowIds?: unknown[] };
        if (obj.scope === 'allRows') return ['bulk'];
        const rowIds = obj.selectedRowIds;
        return Array.isArray(rowIds) && rowIds.length > 3 ? ['bulk'] : ['write'];
    },
    preview: (input: unknown) => {
        const obj = (input ?? {}) as { scope?: unknown; selectedRowIds?: unknown };
        const count = Array.isArray(obj.selectedRowIds) ? obj.selectedRowIds.length : 0;
        const scope = obj.scope === 'allRows' ? 'all rows' : `${count} row${count === 1 ? '' : 's'}`;
        return { summary: `Duplicate ${scope}`, counts: { rows: count } };
    },
};

export const listWorkflowsTool: AgentToolDefinition = {
    name: 'listWorkflows',
    displayName: 'List workflows',
    description: 'List all available workflows in the system. Returns slim summaries, one per workflow: `{ docId, shortLabel, longDescription?, triggerType, applicableFormTypes?, applicableTableTypes?, actionCount, userParameterIds }`. Actions and user parameter definitions are NOT included; call `getWorkflow` with the `docId` to read them.',
    parameters: [
        {
            name: 'limit',
            type: 'number',
            description: 'Optional limit on number of workflows to return (default: 100)',
            required: false,
        },
    ],
    scopes: ['settingsWorkflows', 'formVersion'],
};

export const getFormTool: AgentToolDefinition = {
    name: 'getForm',
    displayName: 'Read form',
    description: 'Get the form, form version, and form-type definition for the form version bound to the current page (the URL-selected version, not the form\'s `currentVersionId` pointer). Fields, entity references, and version state live on the returned `formVersion`, not on `form` itself. Returns LEAN by default: `{ form: {docId, title, formNumber, formTypeId, currentVersionId, assignedTo}, formVersion: {docId, state, fields, entityReferences, lastModified}, formType: {docId, description, possibleStates, fields, entityReferences, tables} }`. Each `formVersion.fields[id]` is `{ value, shortLabel? }`; each `formType.fields[id]` is `{ shortLabel? }`; each `formType.tables[id]` is `{ label?, columns: [{ typeId, label?, kind, fieldDataTypeId?, referenceType?, entityId?, entityGroupId? }] }`. Each `entityReferences[key]` includes the linked record\'s `entityRecordName` when set. Timestamps are ISO strings; UI layout, audit metadata, ACL/index data are NOT included. Pass `verbosity: "full"` ONLY when (1) the user explicitly asks for a property absent from the lean shape (e.g. UI layout, audit timestamps, access groups), (2) you tried with the lean view and discovered a missing property essential to the task, or (3) the user is debugging. Do not request "full" speculatively — full payloads are large and cost the user money.',
    parameters: [
        {
            name: 'verbosity',
            type: 'string',
            description: 'Optional: "lean" (default) returns the lean projection; "full" returns the unmodified document. Use "full" only on explicit need.',
            required: false,
        },
    ],
    scopes: ['formVersion'],
};

/**
 * Union type for workflow tool requests
 */
export type WorkflowToolRequest =
  | {
    /** List workflows task */
    taskType: 'listWorkflows';
    /** Optional limit on number of workflows to return */
    limit?: number;
  }
  | {
    /** Get single workflow task */
    taskType: 'getWorkflow';
    /** Workflow document ID */
    workflowId: DocId;
  }
  | {
    /** Create workflow task */
    taskType: 'createWorkflow';
    /** Workflow data to create (without docId and meta) */
    workflowData: Omit<Workflow, 'docId' | 'meta'>;
  }
  | {
    /** Update workflow task */
    taskType: 'updateWorkflow';
    /** Workflow document ID */
    workflowId: DocId;
    /** Workflow data to update (without docId and meta) */
    workflowData: Omit<Workflow, 'docId' | 'meta'>;
    /** The `meta.lastModified` the caller read; the update is refused when it no longer matches */
    expectedLastModified?: unknown;
  }
  | {
    /** Delete workflow task */
    taskType: 'deleteWorkflow';
    /** Workflow document ID */
    workflowId: DocId;
  };

/**
 * Union type for workflow tool results
 */
export type WorkflowToolResult =
  | { success: true; data: WorkflowSummary[] }
  | { success: true; data: Workflow }
  | { success: true; data: { deleted: true; workflowId: DocId } }
  | { success: false; error: string };

// ─── Agentic Mode ────────────────────────────────────────────────────────────

/**
 * Completion status of an agentic run
 */
export type AgenticFinishStatus = 'success' | 'failure' | 'partial';

/**
 * Configuration for agentic mode
 */
export interface AgenticModeConfig {
    /** The objective the agent should pursue */
    objective: string;
    /** Maximum iterations (LLM round-trips) allowed */
    maxIterations?: number;
}

/**
 * Result returned by the finish tool
 */
export interface AgenticFinishResult {
    /** Whether the agent succeeded, failed, or partially completed */
    status: AgenticFinishStatus;
    /** Human-readable summary of what was accomplished */
    summary: string;
}

export interface AgenticIterationStartEvent {
    iteration: number;
    maxIterations: number;
}

export interface AgenticTextEvent {
    content: string;
}

export interface AgenticToolCallEvent {
    iteration: number;
    toolName: string;
    toolCallId: string;
    args: Record<string, unknown>;
}

export interface AgenticToolResultEvent {
    iteration: number;
    toolCallId: string;
    toolName: string;
    truncated: boolean;
    /** What a `runNamedAnalyticsQuery` call read; absent for every other tool and for a refused call. */
    analyticsQuery?: AnalyticsQueryProvenance;
}

export interface AgenticFinishEvent {
    status: AgenticFinishStatus;
    summary: string;
    totalIterations: number;
    totalToolCalls: number;
}

export interface AgenticErrorEvent {
    message: string;
}

/**
 * Write-volume preview for the staged apply tools (`applyToRecords`/
 * `applyToDocument`), resolved server-side before the approval gate by running
 * the SAME effective-plan resolver the apply uses, so the preview reflects the
 * user's Mapping Review `decisions` overlay (rejected rows, removed/retargeted
 * tables, removed/added fields and references). `willWrite` is the count of
 * rows that land — `value`/`summary`-tagged and not user-rejected, capped at
 * `AR_TABLE_MAX_ROWS` per table (`junk` rows are skipped). It equals the
 * committed count on a first apply and stays an UPPER BOUND on re-apply, where
 * rows a human already touched are left untouched (unknowable before the
 * write). `rowsIn` is the uncapped source row count; `truncated` flags the cap.
 * `tagCounts` breaks the same capped, non-rejected window down by Typesafe
 * classification (value/summary/junk), so `willWrite === tagCounts.value +
 * tagCounts.summary`.
 */
export interface ApplyWritePreview {
    rowsIn: number;
    willWrite: number;
    truncated: boolean;
    unmappedSourceColumns: string[];
    tagCounts?: Record<RowClassificationTag, number>;
}

/**
 * Rich-review payload attached to `approvalRequired` for the staged apply
 * tools — the deterministic invocation of the Mapping Review UI. Pointers
 * only: the client reads `parsed/labeled.json` and `process/<processIndex>.json`
 * from the per-file temp folder itself; user edits travel back as the staged
 * document's decisions overlay, never through the resolve POST. Events without
 * `review` render the plain inline approval card.
 */
export interface AgenticApprovalReview {
    fileId: string;
    processIndex: number;
}

/**
 * Emitted by the harness before a write-class tool call runs. The client
 * displays an approval modal; on the user's decision it POSTs an
 * `AgenticApprovalResolvedRequest` to the same endpoint to resume the loop.
 */
export interface AgenticApprovalRequiredEvent {
    traceId: string;
    toolCallId: string;
    toolName: string;
    summary: string;
    counts?: Record<string, number>;
    reasons: ApprovalReason[];
    /** Write-volume preview for bulk apply tools, resolved server-side before the approval gate. Surfaced in the approval dialog. */
    preview?: ApplyWritePreview;
    /** Present on apply tools: opens the Mapping Review UI instead of the plain approval card. */
    review?: AgenticApprovalReview;
}

/**
 * Emitted when a pending approval is resolved without a client decision — the
 * `APPROVAL_TIMEOUT_MS` window elapsed. The loop injects a cancelled tool
 * result and continues; this frame tells the client to drop the now-stale
 * approval prompt immediately rather than waiting for the next `finish` frame.
 */
export interface AgenticApprovalCancelledEvent {
    traceId: string;
    toolCallId: string;
}

export type AgenticSSEEvent =
    | { type: 'iteration_start'; data: AgenticIterationStartEvent }
    | { type: 'text'; data: AgenticTextEvent }
    | { type: 'tool_call'; data: AgenticToolCallEvent }
    | { type: 'tool_result'; data: AgenticToolResultEvent }
    | { type: 'finish'; data: AgenticFinishEvent }
    | { type: 'error'; data: AgenticErrorEvent }
    | { type: 'approvalRequired'; data: AgenticApprovalRequiredEvent }
    | { type: 'approvalCancelled'; data: AgenticApprovalCancelledEvent };

/**
 * Sibling POST request body the client sends to `/runlanguagemodel` when the
 * user resolves an approval prompt. The handler is keyed by `traceId` and
 * `toolCallId`; on approve the staged tool runs and the loop continues, on
 * cancel the harness injects a `{ ok: false, cancelled: true }` tool result
 * so the model can replan or `finish` — the loop is not killed.
 *
 * `userNavigatedAway` — flipped by the React side when the user has moved off
 * the page the loop was bound to. The next tool result the model sees carries
 * the flag; the agent decides whether to `finish`.
 *
 * `userCancelled` — set when the React side aborts a live stream while an
 * approval is pending. Treated the same as `approved: false` on the wire; the
 * cloud side surfaces it for telemetry but does not branch on it.
 */
export interface AgenticApprovalResolvedRequest {
    approvalResolved: {
        traceId: string;
        toolCallId: string;
        approved: boolean;
        userNavigatedAway?: boolean;
        userCancelled?: boolean;
        /**
         * formVersion-bound apply only: create a new version of the bound
         * form first and retarget the staged apply (and the rest of the run)
         * at it. The worker validates the state transition.
         */
        createNewFormVersion?: { newState: FormStateId };
    };
}

/**
 * Response body of the `approvalResolved` POST. `newFormVersionId` is set when
 * `createNewFormVersion` was honored — the client navigates to it.
 */
export interface AgenticApprovalResolvedResponse {
    success: boolean;
    resolved: boolean;
    reason?: string;
    newFormVersionId?: string;
}

/**
 * Tool definitions for workflow CRUD (used in both one-shot and agentic modes)
 */
export const getWorkflowTool: AgentToolDefinition = {
    name: 'getWorkflow',
    displayName: 'Read workflow',
    description: 'Get a single workflow by its ID. Returns LEAN by default: `{ workflow: {docId, description, icon, triggerType, applicableFormTypes, applicableTableTypes, actions, userParameters, splitScope, maxSelectedRows, lastModified} }` — `lastModified` is the ISO stamp to send back as `expectedLastModified` on `updateWorkflow`; other audit metadata is NOT included. Pass `verbosity: "full"` only on explicit need (debugging, viewing meta).',
    parameters: [
        {
            name: 'workflowId',
            type: 'string',
            description: 'The workflow document ID',
            required: true,
        },
        {
            name: 'verbosity',
            type: 'string',
            description: 'Optional: "lean" (default) or "full". Use "full" only on explicit need.',
            required: false,
        },
    ],
    scopes: ['settingsWorkflows', 'formVersion'],
};

export const createWorkflowTool: AgentToolDefinition = {
    name: 'createWorkflow',
    displayName: 'Create workflow',
    description: 'Create a new workflow definition. Returns ack `{ ok: true, workflowId, lastModified }` — send `lastModified` as `expectedLastModified` on a follow-up `updateWorkflow`. Follow up with `getWorkflow` if you need to verify the created definition.',
    parameters: [
        {
            name: 'workflowData',
            type: 'object',
            description: 'The workflow data to create (name, triggers, actions, etc.)',
            required: true,
            schema: workflowDataJsonSchema,
        },
    ],
    scopes: ['settingsWorkflows'],
    requires: ['workflowsAdmin', 'formsAdmin', 'orgsAdmin'],
    requiresApproval: ['permanent'],
    preview: (input: unknown) => {
        const obj = (input ?? {}) as { workflowData?: { description?: { shortLabel?: unknown } } };
        const label = typeof obj.workflowData?.description?.shortLabel === 'string'
            ? obj.workflowData.description.shortLabel
            : 'new workflow';
        return { summary: `Create workflow "${label}"` };
    },
};

export const updateWorkflowTool: AgentToolDefinition = {
    name: 'updateWorkflow',
    displayName: 'Update workflow',
    description: 'Update an existing workflow definition by ID. Returns ack `{ ok: true, workflowId, lastModified }`.',
    parameters: [
        {
            name: 'workflowId',
            type: 'string',
            description: 'The workflow document ID to update',
            required: true,
        },
        {
            name: 'workflowData',
            type: 'object',
            description: 'The updated workflow data',
            required: true,
            schema: workflowDataJsonSchema,
        },
        {
            name: 'expectedLastModified',
            type: 'string',
            required: false,
            description: 'The `lastModified` that `getWorkflow` returned for this workflow (or the previous `updateWorkflow` ack). Required whenever the read returned one; the update is refused with failed-precondition if the workflow changed since, or if this is missing while the workflow carries a stamp. Omit only when getWorkflow returned no lastModified.',
        },
    ],
    scopes: ['settingsWorkflows'],
    requires: ['workflowsAdmin', 'formsAdmin', 'orgsAdmin'],
    requiresApproval: ['permanent'],
    preview: (input: unknown) => {
        const id = (input as { workflowId?: unknown })?.workflowId;
        return { summary: `Update workflow ${typeof id === 'string' ? id : ''}`.trim() };
    },
};

export const deleteWorkflowTool: AgentToolDefinition = {
    name: 'deleteWorkflow',
    displayName: 'Delete workflow',
    description: 'Delete a workflow definition by ID. Returns ack `{ ok: true, deletedId }`.',
    parameters: [
        {
            name: 'workflowId',
            type: 'string',
            description: 'The workflow document ID to delete',
            required: true,
        },
    ],
    scopes: ['settingsWorkflows'],
    requires: ['workflowsAdmin', 'formsAdmin', 'orgsAdmin'],
    requiresApproval: ['destructive'],
    preview: (input: unknown) => {
        const id = (input as { workflowId?: unknown })?.workflowId;
        return { summary: `Delete workflow ${typeof id === 'string' ? id : ''}`.trim() };
    },
};

export const setFormStateTool: AgentToolDefinition = {
    name: 'setFormState',
    displayName: 'Change form status',
    description: 'Change the state of the form version bound to the current page (e.g., draft → complete). Returns ack `{ ok: true, formVersionId, state }`.',
    parameters: [
        {
            name: 'newState',
            type: 'string',
            description: 'The target state (e.g., "draft", "complete", "on-hold", "scrapped", "lost")',
            required: true,
        },
    ],
    scopes: ['formVersion'],
    requires: ['formsWrite', 'formsAdmin', 'orgsAdmin'],
    requiresApproval: ['permanent'],
    preview: (input: unknown) => {
        const next = (input as { newState?: unknown })?.newState;
        return { summary: `Set form state to "${typeof next === 'string' ? next : '?'}"` };
    },
};

export const executeWorkflowTool: AgentToolDefinition = {
    name: 'executeWorkflow',
    displayName: 'Run workflow',
    description: 'Execute a pre-defined workflow against the form version bound to the current page (the URL-selected version, not the form\'s `currentVersionId` pointer). Delegates to the workflow execution engine which supports all action types (updateCells, updateTableRows, createForm, copyRows, send).',
    parameters: [
        {
            name: 'workflowId',
            type: 'string',
            description: 'The workflow document ID to execute',
            required: true,
        },
        {
            name: 'tableId',
            type: 'string',
            description: 'Optional source table ID for table-based workflows',
            required: false,
        },
        {
            name: 'selectedRowIds',
            type: 'array',
            items: { type: 'string' },
            description: 'Optional array of row IDs to process',
            required: false,
        },
        {
            name: 'userParameters',
            type: 'object',
            description: 'Optional user parameters for the workflow',
            required: false,
        },
    ],
    scopes: ['formVersion'],
    requires: ['formsWrite', 'formsAdmin', 'orgsAdmin'],
    requiresApproval: ['permanent'],
    preview: (input: unknown) => {
        const obj = (input ?? {}) as { workflowId?: unknown; selectedRowIds?: unknown };
        const id = typeof obj.workflowId === 'string' ? obj.workflowId : '?';
        const rows = Array.isArray(obj.selectedRowIds) ? obj.selectedRowIds.length : 0;
        const suffix = rows > 0 ? ` against ${rows} row${rows === 1 ? '' : 's'}` : '';
        return { summary: `Execute workflow ${id}${suffix}` };
    },
};

export const extractFromFileTool: AgentToolDefinition = {
    name: 'extractFromFile',
    displayName: 'Extract data from file',
    description: 'Extract structured data from a tabular or document file (CSV, XLSX, JSON, XML, TXT, or PDF/DOCX/PPTX). Takes the file\'s `fileId` (surfaced in the attachment prefix as `<file_id>`) and returns `{ fileId, description, chunks, labels, parser }`. The same `fileId` is a durable handle accepted by every other file tool (`readExtractedFile`, `findPartsBatch`, `proposeImport`, `applyToDocument`, `applyToRecords`) — this turn and every later turn. `labels` is the agent\'s column-label catalog for the file (per-chunk). Each table chunk includes source columns, up to 20 sample rows, and total row count. Spreadsheets, CSV, JSON, XML and TXT are read by the built-in parser; if it cannot read the file, this tool returns a clear error (HTTP 422) naming the reason — it does NOT silently produce image descriptions. PDF, DOCX and PPTX are read by the document-AI extractor, which OCRs scanned and image-only pages, so a scanned document still returns its text and tables. Note: email files (EML, MSG) are pre-parsed at upload time — call this tool on each attachment\'s fileId rather than on the email itself.',
    parameters: [
        { name: 'fileId', type: 'string', description: 'The file\'s durable handle, surfaced in the attachment prefix as `<file_id>`.', required: true },
    ],
    scopes: ['global'],
};

export const startPdfIntakeTool: AgentToolDefinition = {
    name: 'startPdfIntake',
    displayName: 'Start PDF intake',
    description: 'Read a PDF into the form version bound to the current page through PDF intake: every cell is read and verified against the page, staged for that form and routed for review, all within this call. Takes the PDF\'s `fileId` (surfaced as `<file_id>`). The order form is bound from the page and is not an input; with no form open it refuses. Returns `{ jobId, status, route, pages, flaggedCells, failedPages, alreadyRead, href }`; `alreadyRead` true means the same PDF was read before and this is that job. Use this instead of `extractFromFile` for every PDF, and never follow it with `proposeImport` or `applyToDocument`: the user reviews the schedule at `href`. Reply in one short message with pages read, flagged cells and failed pages, and link `href` as a markdown link.',
    parameters: [
        { name: 'fileId', type: 'string', description: 'The PDF\'s durable handle, surfaced in the attachment prefix as `<file_id>`.', required: true },
    ],
    scopes: ['global', 'formVersion'],
    requires: ['aiOperator', 'aiAdmin'],
    pdfExtractionPath: 'pdfIntake',
};

export const readExtractedFileTool: AgentToolDefinition = {
    name: 'readExtractedFile',
    displayName: 'Read extracted file',
    description: 'Read sections of a file you ALREADY parsed with `extractFromFile`, straight from the durable per-file folder — cheap, read-only, never re-parses. Keyed on the file\'s `fileId` (surfaced as `<file_id>`), so it works across turns. Two modes: (1) OMIT both `chunkId` and `groupId` to get the catalog — one entry per chunk with `chunkId`, `chunkGroupId`, `description`, `kind`, `provenance`, table `sourceColumns`/`totalRowCount` or text `charCount`, and `tagCounts` when a `proposeImport` doc covers it; never returns rows. (2) PASS exactly one of `chunkId` (one fragment) or `groupId` (the whole section — every fragment of e.g. a table spanning pages, joined in order) to read that selection: tables return a `rows[offset, offset+limit)` window with `columns`, `returnedCount`, `totalRowCount`, and `nextOffset` when more remain; text returns a `textSlice[offset, offset+limit)` char window. For a table selection pass `tag` ("value" | "summary" | "junk") to return only rows with that classification (requires a `proposeImport` doc for that selection). Use this to page rows, read text bodies, or filter by tag without paying for another parse.',
    parameters: [
        { name: 'fileId', type: 'string', description: 'The file\'s durable handle, surfaced as `<file_id>`. Must already be parsed by `extractFromFile`.', required: true },
        { name: 'chunkId', type: 'string', description: 'Optional. Read one fragment, by its catalog `chunkId` (label.id). Provide at most one of `chunkId`/`groupId`; omit both for the catalog.', required: false },
        { name: 'groupId', type: 'string', description: 'Optional. Read a whole section, by its catalog `chunkGroupId` (joins every fragment in order). Provide at most one of `chunkId`/`groupId`; omit both for the catalog.', required: false },
        { name: 'offset', type: 'number', description: 'Optional window start (rows for tables, characters for text). Defaults to 0.', required: false },
        { name: 'limit', type: 'number', description: 'Optional window size (rows for tables, characters for text). Capped server-side.', required: false },
        { name: 'tag', type: 'string', description: 'Optional table-only filter: "value", "summary", or "junk". Requires a proposeImport process document for the section.', required: false },
    ],
    scopes: ['global'],
};

export const getSchemaTool: AgentToolDefinition = {
    name: 'getSchema',
    displayName: 'Read schema',
    description: 'Read the schema definition for an entity, table type, or form type. Use this to learn target field IDs, labels, and data types before mapping source columns. Entity and form-type results include `fields` (with `shortLabel`), `entityReferences`, and `tables` (each with `label` and `columns`).',
    parameters: [
        { name: 'schemaType', type: 'string', description: 'One of "entity", "tableType", or "formType"', required: true },
        { name: 'id', type: 'string', description: 'The document ID of the entity, tableType, or formType', required: true },
    ],
    scopes: ['global', 'fallback'],
};

export const getFieldDetailsTool: AgentToolDefinition = {
    name: 'getFieldDetails',
    displayName: 'Read field details',
    description: 'Look up the full description of one or more fields by `fieldId` — returns `shortLabel`, `longLabel`, `longDescription`, `errorMessage`, prefix/suffix, and `fieldDataTypeId`. USE THIS TOOL ONLY when the user asks to know more about a specific field (its purpose, format, valid values, examples). Do not call it for routine reads — `getForm` and `getRecord` already include `shortLabel` per field.',
    parameters: [
        { name: 'fieldIds', type: 'array', items: { type: 'string' }, description: 'The field IDs to describe.', required: true },
    ],
    scopes: ['global', 'fallback'],
};

export const createFormTool: AgentToolDefinition = {
    name: 'createForm',
    displayName: 'Create form',
    description: 'Create a new form of the given form type. Optionally seed initial form-level fields and entity references. Returns LEAN by default: `{ formId, formVersionId, form, formVersion, formType }` (lean projections) — a strict superset of `getForm`, so no follow-up read is needed to verify seeded state. Pass `verbosity: "full"` only on explicit need (UI layout, audit metadata, etc.) — full payloads are large and cost the user money.',
    parameters: [
        { name: 'formType', type: 'string', description: 'The form type document ID', required: true },
        { name: 'title', type: 'string', description: 'Optional title for the new form', required: false },
        { name: 'fields', type: 'object', description: 'Optional initial form-level fields, keyed by field ID. Each value should shape as { value, description? }.', required: false },
        { name: 'entityReferences', type: 'object', description: 'Optional initial entity references, keyed by entity/group ID. Each value should shape as { entityRecordId, entityRecordName?, entityGroupId? }.', required: false },
        { name: 'verbosity', type: 'string', description: 'Optional: "lean" (default) or "full". Use "full" only on explicit need.', required: false },
    ],
    scopes: ['forms', 'home'],
    requires: ['formsWrite', 'formsAdmin', 'orgsAdmin'],
    requiresApproval: ['write'],
    preview: (input: unknown) => {
        const obj = (input ?? {}) as { formType?: unknown; title?: unknown };
        const ft = typeof obj.formType === 'string' ? obj.formType : '?';
        const title = typeof obj.title === 'string' && obj.title.length > 0 ? ` titled "${obj.title}"` : '';
        return { summary: `Create a new ${ft} form${title}` };
    },
};

export const proposeImportTool: AgentToolDefinition = {
    name: 'proposeImport',
    displayName: 'Plan data import',
    description: 'Stage an extraction for the bound document. Reads the file named by `fileId` and writes ONE process document at `process/<n>.json` in the file\'s per-file folder; every mapping and score in it is model-judged (Typesafe) — you never author values, mappings, or scores. Target shape is derived from the bound page. On a `formVersion`/`record` page the WHOLE document is staged from the whole file — no chunk selection: header fields and entity references are mapped from extracted label/value facts, and every table section is assigned to a target table on the bound document, each with per-column mappings, cleaning rules, reference resolutions, and per-row value/summary/junk tags (nothing is dropped). On an `entity` page, pass exactly one of `chunkId`/`groupId` to stage that table section as records. Returns `{ processIndex, summary, columns? }` (`columns` on entity stagings only — flag low-confidence mappings to the user). Non-committing — pair `processIndex` with `applyToDocument` (formVersion/record) or `applyToRecords` (entity) after user approval; the user reviews and edits the staged mapping in the Mapping Review UI during that approval.',
    parameters: [
        { name: 'fileId', type: 'string', description: 'The file\'s durable handle, surfaced as `<file_id>` (from `extractFromFile`).', required: true },
        { name: 'chunkId', type: 'string', description: 'Entity page only: stage one fragment, by its catalog `chunkId` (label.id). Provide exactly one of `chunkId`/`groupId`. Ignored on formVersion/record pages — the whole file is staged.', required: false },
        { name: 'groupId', type: 'string', description: 'Entity page only: stage a whole section as one table, by its catalog `chunkGroupId` (joins every fragment, e.g. a table spanning pages). Provide exactly one of `chunkId`/`groupId`. Ignored on formVersion/record pages — the whole file is staged.', required: false },
        { name: 'locale', type: 'string', description: 'Optional numeric-format convention of the source document: "commaDecimal" (1.234,56 / 1 234,56 — most of Europe), "dotDecimal" (1,234.56 — US/UK), or "indianComma" (Indian lakh/crore grouping, 1,23,456.78). Set it when the document\'s number format is known from its language, origin, or the user\'s instruction; when the user asks for a specific interpretation, pass their choice. Omit to resolve separators structurally — a value that stays ambiguous is imported as blank with the raw text kept for review.', required: false, schema: { type: 'string', enum: ['dotDecimal', 'commaDecimal', 'indianComma'] } },
        { name: 'dateLocale', type: 'string', description: 'Optional date-order convention of the source document: "dayFirst" (DD/MM/YYYY — most of the world), "monthFirst" (MM/DD/YYYY — US), or "yearFirst" (year-first / ISO order, YYYY-MM-DD). Set it when the date order is known from the document language, origin, or the user\'s instruction. Omit to resolve order from whole-column evidence; a date that stays ambiguous is imported blank with the raw text kept for review.', required: false, schema: { type: 'string', enum: ['dayFirst', 'monthFirst', 'yearFirst'] } },
    ],
    scopes: ['formVersion', 'record', 'entity'],
};

export const applyToRecordsTool: AgentToolDefinition = {
    name: 'applyToRecords',
    displayName: 'Import rows into records',
    description: 'Materialize the rows of a parsed file (`fileId` from `extractFromFile`) into records of the bound entity. Reads the process document named by `processIndex` (from `proposeImport`) server-side: it writes EVERY row (each carrying its Typesafe value/summary/junk tag — nothing is dropped) after running each column\'s cleaning rules. During approval the user reviews and may edit the proposal (remap/unmap columns, edit cleaning rules, override reference resolutions and cells, reject rows); those decisions are honored at write — user-rejected rows are skipped. Records are ALWAYS created with status `Pending` — promote later via `setRecordsStatus`. The bound `entityId` is injected from page context; not an input. Returns `{ ok, recordsWritten, summary }`.',
    parameters: [
        { name: 'fileId', type: 'string', description: 'The file\'s durable handle, surfaced as `<file_id>` (from `extractFromFile`).', required: true },
        { name: 'processIndex', type: 'number', description: 'Index (n) of the process doc at `<fileFolder>/process/<n>.json` from `proposeImport`.', required: true },
    ],
    scopes: ['entity'],
    requires: ['recordsWrite', 'recordsAdmin', 'orgsAdmin'],
    requiresApproval: ['bulk'],
    preview: () => ({ summary: 'Create records on the bound entity (status Pending).' }),
};

export const searchTool: AgentToolDefinition = {
    name: 'search',
    displayName: 'Search',
    description: 'Free-text search across documents (Algolia), available on every page. Returns hits tagged by `kind`, each carrying the ids needed to read or link it: `record` → `{ recordId, entityId, name, status? }`; `form` → `{ formId, formVersionId, formNumber?, formType?, state? }`; `tableRow` → `{ tableId, rowId, parentArtifactType, formId?, entityId?, title, subtitle? }`; `attachment` → `{ attachmentId, title, subtitle? }`. Pass `documentType` to restrict to one kind; omit to search all. To resolve a reference for linking, search the target entity\'s records: pass that entity via `entityId` (or `entityName` if you only know its label) — the reference\'s target entity comes from the form/record schema, e.g. via getForm. Record hits are ranked by best name match for the query. The record narrowing args (`entityId`/`entityName`, `entityGroupId`/`entityGroupName`, `status`) scope the search to records; `status` filters Active | Inactive | Pending. Results respect the user\'s read permission per kind.',
    parameters: [
        { name: 'query', type: 'string', description: 'Free-text query to look up.', required: true },
        { name: 'documentType', type: 'string', description: 'Optional kind filter: "record", "form", "tableRow", or "attachment". Omit to search all kinds.', required: false },
        { name: 'entityId', type: 'string', description: 'Optional. The entity whose records to search — the reference target entity from the slot schema. Pass it (or `entityName`) to scope a record search to one entity.', required: false },
        { name: 'entityName', type: 'string', description: 'Optional alternative to `entityId`: the entity\'s human label. Resolved to its id server-side. Pass either `entityId` or `entityName`, not both.', required: false },
        { name: 'entityGroupId', type: 'string', description: 'Optional entity-group filter for records.', required: false },
        { name: 'entityGroupName', type: 'string', description: 'Optional alternative to `entityGroupId`: the group\'s human label, resolved within the scoped entity. Requires `entityId` or `entityName`.', required: false },
        { name: 'status', type: 'string', description: 'Optional record status filter: "Active", "Inactive", or "Pending".', required: false },
        { name: 'limit', type: 'number', description: 'Max hits to return (default 25, max 50).', required: false },
    ],
    scopes: ['global'],
};

export const listFormsTool: AgentToolDefinition = {
    name: 'listForms',
    displayName: 'List forms',
    description: 'List forms the user has access to, ordered by most recently modified. Use when the user asks about "their forms", "recent forms", or to find a form by its number. Returns slim summaries (formId, formNumber, formType, assignedTo, currentVersionId, lastModified). Forms are identified to the user by their formNumber (e.g. "REQ-001"); use getSchema with schemaType="formType" to resolve a formType ID to a human description. Call getForm to read a single form in depth (form-version state, fields, and entity references live on the version, not the form).',
    parameters: [
        { name: 'limit', type: 'number', description: 'Maximum number of forms to return (default 50, max 200).', required: false },
        { name: 'formType', type: 'string', description: 'Optional formType document ID to filter by.', required: false },
        { name: 'assignedTo', type: 'string', description: 'Optional user uid to filter by — matches forms whose `assignedTo` field equals this id.', required: false },
        { name: 'daysBack', type: 'number', description: 'How far back to search (in days, default 3650 = ~10 years, max 3650).', required: false },
    ],
    scopes: ['global', 'fallback'],
};

export const notifyUserTool: AgentToolDefinition = {
    name: 'notifyUser',
    displayName: 'Notify me',
    description: 'Send a notification to the user via the notification system. Use this to inform the user about completed tasks, important updates, or when you need their attention on something. The notification will appear in the Notifications page.',
    parameters: [
        { name: 'message', type: 'string', description: 'The notification message', required: true },
    ],
    scopes: ['global'],
};

export const sendEmailTool: AgentToolDefinition = {
    name: 'sendEmail',
    displayName: 'Send email',
    description: 'Draft and send an email through the organization\'s configured sender. Use to reply to a supplier, confirm a purchase order, notify a colleague, or send any outbound message on the user\'s behalf. The body must be HTML; supply a plain-text alternative when possible. The organization\'s email policy controls whether replies route back to the user — pass replyTo only when the user has explicitly asked for replies to come to them.',
    parameters: [
        { name: 'to', type: 'array', items: { type: 'string' }, description: 'Recipient email addresses', required: true },
        { name: 'subject', type: 'string', description: 'Email subject line', required: true },
        { name: 'html', type: 'string', description: 'Email body as HTML', required: true },
        { name: 'text', type: 'string', description: 'Optional plain-text alternative for clients that do not render HTML', required: false },
        { name: 'cc', type: 'array', items: { type: 'string' }, description: 'CC recipients', required: false },
        { name: 'bcc', type: 'array', items: { type: 'string' }, description: 'BCC recipients', required: false },
        { name: 'replyTo', type: 'string', description: 'Reply-to address. Verified server-side against the calling user\'s email; ignored when the org policy forbids user replies.', required: false },
        { name: 'formId', type: 'string', description: 'Optional form document ID to associate the email with for telemetry and organization-policy resolution', required: false },
    ],
    scopes: ['global'],
    requires: ['formsWrite', 'recordsWrite', 'formsAdmin', 'recordsAdmin', 'orgsAdmin'],
    requiresApproval: ['permanent'],
    preview: (input: unknown) => {
        const obj = (input ?? {}) as { to?: unknown; subject?: unknown };
        const to = Array.isArray(obj.to) ? obj.to.length : 0;
        const subj = typeof obj.subject === 'string' ? obj.subject : '(no subject)';
        return { summary: `Send email "${subj}" to ${to} recipient${to === 1 ? '' : 's'}`, counts: { recipients: to } };
    },
};

export const computeAggregationsTool: AgentToolDefinition = {
    name: 'computeAggregations',
    displayName: 'Recalculate totals',
    description: 'Recompute aggregated values (sums, counts, etc.) for the columns of a table on the bound form version or record. The aggregation kinds are defined on the table type. Returns the persisted aggregation cells. Use this when the user asks "what\'s the total of this column", "how many rows match X", or after bulk-editing rows when aggregations may be stale.',
    parameters: [
        { name: 'tableId', type: 'string', description: 'The table ID (table type) within the bound document.', required: true },
    ],
    scopes: ['formVersion', 'record'],
};

export const deleteTableRowsTool: AgentToolDefinition = {
    name: 'deleteTableRows',
    displayName: 'Delete table rows',
    description: 'Delete one or more rows from a table on the bound form version or record. Irreversible. Use when the user asks to "remove row(s)", "drop the line item", etc. Returns counts of affected and skipped rows.',
    parameters: [
        { name: 'tableId', type: 'string', description: 'The table ID (table type) within the bound document.', required: true },
        { name: 'rowIds', type: 'array', items: { type: 'string' }, description: 'IDs of the rows to delete.', required: true },
    ],
    scopes: ['formVersion', 'record'],
    requires: ['formsWrite', 'recordsWrite', 'formsAdmin', 'recordsAdmin', 'orgsAdmin'],
    requiresApproval: ['destructive', 'bulk'],
    preview: (input: unknown) => {
        const obj = (input ?? {}) as { tableId?: unknown; rowIds?: unknown };
        const tableId = typeof obj.tableId === 'string' ? obj.tableId : '<table>';
        const count = Array.isArray(obj.rowIds) ? obj.rowIds.length : 0;
        return { summary: `Delete ${count} row${count === 1 ? '' : 's'} from "${tableId}"`, counts: { rows: count } };
    },
};

export const exportTableCsvTool: AgentToolDefinition = {
    name: 'exportTableCsv',
    displayName: 'Export table to CSV',
    description: 'Export every row of a table on the bound form version or record as structured row data the agent can render or pass to other tools. Returns rows ordered by the requested field. The agent should serialize the result to CSV when presenting to the user.',
    parameters: [
        { name: 'tableId', type: 'string', description: 'The table ID (table type) within the bound document.', required: true },
        { name: 'orderByField', type: 'string', description: 'Field path to order rows by (e.g. "order" or "cells.lineItem.value").', required: true },
        { name: 'orderByDirection', type: 'string', description: 'Order direction: "asc" or "desc".', required: true },
    ],
    scopes: ['formVersion', 'record'],
};

export const createAttachmentLinkTool: AgentToolDefinition = {
    name: 'createAttachmentLink',
    displayName: 'Create attachment link',
    description: 'Generate a shareable URL for one or more attachments. Returns per-file shareUrl + expirationTime. Use when the user asks to "share this file", "send a link to the spec sheet", etc.',
    parameters: [
        { name: 'files', type: 'array', items: { type: 'object' }, description: 'Array of `{ fileId, versionId, artifactPath? }`. fileId is the attachment document id; versionId is the attachment version to share; artifactPath optionally points to the form/entity that references this attachment.', required: true },
        { name: 'expirationTime', type: 'number', description: 'Optional expiration as ms since epoch. Applies to all files. Defaults to a server-side default (typically 7 days).', required: false },
    ],
    scopes: ['formVersion', 'record'],
    requires: ['attachmentsWrite', 'orgsAdmin'],
    requiresApproval: ['write'],
    preview: (input: unknown) => {
        const files = (input as { files?: unknown })?.files;
        const count = Array.isArray(files) ? files.length : 0;
        return { summary: `Create share link for ${count} attachment${count === 1 ? '' : 's'}`, counts: { attachments: count } };
    },
};

export const revokeAttachmentLinkTool: AgentToolDefinition = {
    name: 'revokeAttachmentLink',
    displayName: 'Revoke attachment link',
    description: 'Revoke a previously generated shareable link for an attachment. The link will stop working immediately. Returns ack `{ ok: true }`.',
    parameters: [
        { name: 'fileId', type: 'string', description: 'The attachment document id.', required: true },
        { name: 'versionId', type: 'string', description: 'The attachment version id whose share link should be revoked.', required: true },
        { name: 'artifactPath', type: 'string', description: 'Optional path to the form or entity that references the attachment.', required: false },
    ],
    scopes: ['formVersion', 'record'],
    requires: ['attachmentsDelete', 'orgsAdmin'],
    requiresApproval: ['destructive'],
    preview: (input: unknown) => {
        const id = (input as { fileId?: unknown })?.fileId;
        return { summary: `Revoke share link for ${typeof id === 'string' ? id : 'attachment'}` };
    },
};

export const createFormVersionTool: AgentToolDefinition = {
    name: 'createFormVersion',
    displayName: 'Create form version',
    description: 'Create a new draft version of an existing form, copying the source version\'s fields, entity references, and table rows. Use when the user asks to "start a new revision" or "make a draft from this version". Returns ack `{ ok: true, formId, formVersionId }`. Follow up with `getForm` if you need to verify the new version.',
    parameters: [
        { name: 'formId', type: 'string', description: 'The form document id.', required: true },
        { name: 'sourceVersionId', type: 'string', description: 'The form version document id to copy from.', required: true },
        { name: 'newState', type: 'string', description: 'The state for the new version (e.g. "draft").', required: true },
        { name: 'setVersionData', type: 'object', description: 'Optional partial fields to merge into the new version on creation.', required: false },
    ],
    scopes: ['formVersion'],
    requires: ['formsWrite', 'formsAdmin', 'orgsAdmin'],
    requiresApproval: ['write'],
    preview: (input: unknown) => {
        const src = (input as { sourceVersionId?: unknown })?.sourceVersionId;
        return { summary: `Create new draft version from ${typeof src === 'string' ? src : 'source version'}` };
    },
};

export const setCurrentFormVersionTool: AgentToolDefinition = {
    name: 'setCurrentFormVersion',
    displayName: 'Make version current',
    description: 'Set a specific form version as the current/active version of the form. Use when the user asks to "make this version the current one" or "publish this revision". Returns ack `{ ok: true, formId, currentVersionId }`.',
    parameters: [
        { name: 'formId', type: 'string', description: 'The form document id.', required: true },
        { name: 'versionId', type: 'string', description: 'The form version document id to set as current.', required: true },
    ],
    scopes: ['formVersion'],
    requires: ['formsWrite', 'formsAdmin', 'orgsAdmin'],
    requiresApproval: ['permanent'],
    preview: (input: unknown) => {
        const v = (input as { versionId?: unknown })?.versionId;
        return { summary: `Set version ${typeof v === 'string' ? v : '?'} as current` };
    },
};

export const duplicateFormTool: AgentToolDefinition = {
    name: 'duplicateForm',
    displayName: 'Copy form',
    description: 'Duplicate a form (create-from-form) — creates a new form, optionally of a different form type, copying fields, entity references, and table rows from the source version. Use when the user asks to "copy this form", "create a similar PO", or "convert this quote into an order". Returns ack `{ ok: true, formId, formVersionId }`. Follow up with `getForm` to inspect the duplicated state.',
    parameters: [
        { name: 'formId', type: 'string', description: 'The source form document id.', required: true },
        { name: 'sourceVersionId', type: 'string', description: 'The source form version document id to copy from.', required: true },
        { name: 'newFormType', type: 'string', description: 'The form type id of the new form. Pass the same id as the source form\'s type to keep it the same.', required: true },
        { name: 'title', type: 'string', description: 'Optional title for the new form.', required: false },
        { name: 'assignedTo', type: 'string', description: 'Optional uid to assign the new form to.', required: false },
        { name: 'setFormData', type: 'object', description: 'Optional partial Form fields to merge into the new form.', required: false },
        { name: 'setVersionData', type: 'object', description: 'Optional partial FormVersion fields to merge into the new version.', required: false },
    ],
    scopes: ['formVersion'],
    requires: ['formsWrite', 'formsAdmin', 'orgsAdmin'],
    requiresApproval: ['write'],
    preview: (input: unknown) => {
        const obj = (input ?? {}) as { newFormType?: unknown; title?: unknown };
        const ft = typeof obj.newFormType === 'string' ? obj.newFormType : '?';
        const title = typeof obj.title === 'string' && obj.title.length > 0 ? ` titled "${obj.title}"` : '';
        return { summary: `Duplicate this form as a new ${ft}${title}` };
    },
};

export const findPartsTool: AgentToolDefinition = {
    name: 'findParts',
    displayName: 'Find parts',
    description: 'Find the part(s) that best match a free-text request. Searches the customer\'s own parts entity records (Algolia) and/or the Signify catalog in parallel, then asks TypeSafe (`choice` prompt) which candidate satisfies the request best. Returns a slim list of merged candidates plus a `best` pick with confidence. Pass `sources` to scope to one provider; pass `entityId` (the parts entity in this tenant) when including `algolia`. Signify search is free-text across `product12NC`, `commercialCode`, `UPC`, `6NC`, and `description` — descriptive keyword queries (e.g. "linear LED 3200K AC") are supported, not just product codes. Signify is stripped server-side when the session is not Signify-authorized — Algolia-only calls still succeed.',
    parameters: [
        { name: 'query', type: 'string', description: 'Free-text request (e.g. "200W LED downlight 4000K", "Philips part starting with 12X").', required: true },
        { name: 'sources', type: 'array', items: { type: 'string' }, description: 'Subset of ["algolia", "signify"] to query. Defaults to both.', required: false },
        { name: 'entityId', type: 'string', description: 'Required when `sources` includes "algolia" — the entity that holds the customer\'s parts records.', required: false },
        { name: 'entityGroupId', type: 'string', description: 'Optional Algolia entity-group filter.', required: false },
        { name: 'limitPerSource', type: 'number', description: 'Max candidates pulled per source before merging (default 20, max 50).', required: false },
        { name: 'topK', type: 'number', description: 'Max merged candidates returned and judged by TypeSafe (default 5, max 25).', required: false },
    ],
    scopes: ['global'],
};

export const findPartEquivalentsTool: AgentToolDefinition = {
    name: 'findPartEquivalents',
    displayName: 'Find equivalent parts',
    description: 'Find equivalent or replacement parts for an existing part. Pass any combination of `seed.name`, `seed.sku`, `seed.description` — they are concatenated into the query. The seed itself can be excluded via `excludeSourceId`. Same multi-source mechanism as `findParts`; Signify is stripped server-side when the session is not Signify-authorized.',
    parameters: [
        { name: 'seed', type: 'object', description: 'Object with `name`, `sku`, and/or `description` of the seed part. At least one is required.', required: true },
        { name: 'sources', type: 'array', items: { type: 'string' }, description: 'Subset of ["algolia", "signify"] to query. Defaults to both.', required: false },
        { name: 'entityId', type: 'string', description: 'Required when `sources` includes "algolia".', required: false },
        { name: 'entityGroupId', type: 'string', description: 'Optional Algolia entity-group filter.', required: false },
        { name: 'excludeSourceId', type: 'string', description: 'Source id of the seed part itself, to exclude from results (when the seed is also indexed).', required: false },
        { name: 'limitPerSource', type: 'number', description: 'Max candidates pulled per source (default 20, max 50).', required: false },
        { name: 'topK', type: 'number', description: 'Max merged candidates returned (default 5, max 25).', required: false },
    ],
    scopes: ['global'],
};

export const findPartsBatchTool: AgentToolDefinition = {
    name: 'findPartsBatch',
    displayName: 'Match parts from file',
    description: 'Resolve every row of a parsed file (from `extractFromFile`) to a part — by reference. Reads the rows of the file named by `fileId`, builds a per-row seed from `seedMapping`, runs Algolia (and Signify if authorized) per row, judges the merged candidates with TypeSafe, and writes a `PartResolutionsDocument` JSON to `<fileFolder>/partResolutions.json`. Returns `{ ok, summary, skippedSources }` — the candidates themselves stay out of the model context. High-confidence single-source hits are marked `decision: "reference"`; everything else `decision: "manual"` with candidates retained for inspection. Then call `applyToDocument` with `useResolutions: true` to materialize cells.',
    parameters: [
        { name: 'fileId', type: 'string', description: 'The file\'s durable handle, surfaced as `<file_id>` (from `extractFromFile`).', required: true },
        { name: 'chunkId', type: 'string', description: 'Resolve one fragment, by its catalog `chunkId` (label.id). Provide exactly one of `chunkId`/`groupId`.', required: false },
        { name: 'groupId', type: 'string', description: 'Resolve a whole section, by its catalog `chunkGroupId` (joins every fragment of e.g. a table spanning pages). Provide exactly one of `chunkId`/`groupId`.', required: false },
        { name: 'seedMapping', type: 'object', description: 'Object with optional `nameColumn`, `skuColumn`, `descriptionColumn` — at least one is required. Cells from these columns build the per-row search seed.', required: true },
        { name: 'sources', type: 'array', items: { type: 'string' }, description: 'Subset of ["algolia", "signify"]. Defaults to both. Signify is stripped server-side when not authorized for the session.', required: false },
        { name: 'entityId', type: 'string', description: 'Required when `sources` includes "algolia" — the parts entity in this tenant.', required: false },
        { name: 'entityGroupId', type: 'string', description: 'Optional Algolia entity-group filter.', required: false },
        { name: 'limitPerSource', type: 'number', description: 'Max candidates pulled per source per row (default 20, max 50).', required: false },
        { name: 'topK', type: 'number', description: 'Max merged candidates judged per row (default 5, max 25).', required: false },
        { name: 'maxRows', type: 'number', description: 'Cap on rows processed. Defaults to all rows up to AR_TABLE_MAX_ROWS.', required: false },
    ],
    scopes: ['global'],
};

export const getSignifyProductPriceAndAvailabilityTool: AgentToolDefinition = {
    name: 'getSignifyProductPriceAndAvailability',
    displayName: 'Check Signify price and availability',
    description: 'Fetch a Signify product\'s canonical detail, current price, and availability in a single call. `customerId` and `distributorId` shape the price quote. Availability is fetched in parallel and may fail independently — check `availabilityError` if `availability` is null.',
    parameters: [
        { name: 'productCode', type: 'string', description: 'Signify product code (e.g. from `findParts` `sourceId` when source is "signify").', required: true },
        { name: 'customerId', type: 'string', description: 'Optional Signify customer id used to resolve the customer-specific price.', required: false },
        { name: 'distributorId', type: 'string', description: 'Optional Signify distributor id used to resolve the distributor-specific price.', required: false },
    ],
    scopes: ['global'],
    requiresToggle: ['signifyAuthorized'],
};

export const searchSignifyCustomersTool: AgentToolDefinition = {
    name: 'searchSignifyCustomers',
    displayName: 'Find Signify customers',
    description: 'List Signify customers visible to the integrated user. Signify\'s API returns the full list — use `nameContains` to filter client-side. Returns `{ customers, truncated }`.',
    parameters: [
        { name: 'nameContains', type: 'string', description: 'Optional case-insensitive substring filter on customer name.', required: false },
        { name: 'fields', type: 'string', description: 'Detail level: "BASIC", "DEFAULT", or "FULL". Default is the Signify default.', required: false },
        { name: 'filterAgents', type: 'boolean', description: 'When true, exclude agent-type customers.', required: false },
        { name: 'limit', type: 'number', description: 'Max customers to return (default 50, max 200). The truncated flag indicates whether more matched.', required: false },
    ],
    scopes: ['global'],
    requiresToggle: ['signifyAuthorized'],
};

export const searchSignifyDistributorsTool: AgentToolDefinition = {
    name: 'searchSignifyDistributors',
    displayName: 'Find Signify distributors',
    description: 'Look up Signify distributors for a customer. Pass only `customerId` to list distributors (with optional `nameContains` filter). Pass `distributorId` to fetch full details for one distributor — the response shape switches to `{ mode: "details", details }`.',
    parameters: [
        { name: 'customerId', type: 'string', description: 'The Signify customer id whose distributors to query.', required: true },
        { name: 'distributorId', type: 'string', description: 'Optional. When provided, returns full distributor details rather than a list.', required: false },
        { name: 'nameContains', type: 'string', description: 'Optional case-insensitive substring filter on distributor name (list mode only).', required: false },
        { name: 'fields', type: 'string', description: 'Detail level: "BASIC", "DEFAULT", or "FULL".', required: false },
        { name: 'limit', type: 'number', description: 'Max distributors to return in list mode (default 50, max 200).', required: false },
    ],
    scopes: ['global'],
    requiresToggle: ['signifyAuthorized'],
};

export const getRecordTool: AgentToolDefinition = {
    name: 'getRecord',
    displayName: 'Read record',
    description: 'Read the bound entity record. `entityId` + `recordId` are auto-bound from page context. Returns LEAN by default: `{ record: {docId, entityId, name, status, values, entityReferences, entityGroupIds, lastModified} }`. UI layout, audit metadata, attachments details, search-index fields are NOT included. Pass `verbosity: "full"` only on explicit need (e.g. attachments or audit history). Full payloads are large and cost the user money.',
    parameters: [
        { name: 'verbosity', type: 'string', description: 'Optional: "lean" (default) or "full". Use "full" only on explicit need.', required: false },
    ],
    scopes: ['record'],
};

export const createRecordTool: AgentToolDefinition = {
    name: 'createRecord',
    displayName: 'Create record',
    description: 'Create a single new record on the bound entity. `entityId` is auto-bound. The status is always set to `Pending` — promote later via `setRecordStatus`. Returns LEAN by default: `{ ok: true, entityId, recordId, applied, record }` (strict superset of `getRecord`, no follow-up read needed) or `{ ok: false, error, applied, rejected }` on validation failure.',
    parameters: [
        { name: 'name', type: 'string', description: 'Optional display name (falls back to the entity\'s name-from-field convention).', required: false },
        { name: 'fields', type: 'object', description: 'Optional initial fields, keyed by fieldId. Each value shapes as `{ value, description? }` — the server owns audit metadata, do not pass `meta`.', required: false },
        { name: 'entityReferences', type: 'object', description: 'Optional initial entity references, keyed by entity/group ID. Each value shapes as `{ entityRecordId, entityRecordName?, entityGroupId? }`.', required: false },
        { name: 'entityGroupIds', type: 'array', items: { type: 'string' }, description: 'Optional record group memberships.', required: false },
    ],
    scopes: ['entity'],
    requires: ['recordsWrite', 'recordsAdmin', 'orgsAdmin'],
    requiresApproval: ['write'],
    preview: (input: unknown) => {
        const obj = (input as { name?: unknown } | undefined) ?? {};
        const name = typeof obj.name === 'string' && obj.name ? `"${obj.name}"` : 'new record';
        return { summary: `Create ${name}` };
    },
};

export const deleteRecordTool: AgentToolDefinition = {
    name: 'deleteRecord',
    displayName: 'Delete record',
    description: 'Hard-delete the bound entity record. Irreversible — the records-archive procedure is deferred and not exposed to the agent. `entityId` + `recordId` are auto-bound from page context. Returns `{ ok: true, entityId, deletedId }`.',
    parameters: [],
    scopes: ['record'],
    requires: ['recordsDelete', 'recordsAdmin', 'orgsAdmin'],
    requiresApproval: ['destructive'],
    preview: () => ({ summary: 'Delete this record permanently' }),
};

export const listNotificationsTool: AgentToolDefinition = {
    name: 'listNotifications',
    displayName: 'List notifications',
    description: 'List the user\'s unread notifications, most recent first. Returns each notification\'s `eventId`, source bundle id, and the notification payload (event message, form id when relevant, etc.). Use when the user asks "what\'s new", "any unread notifications", or to triage their inbox.',
    parameters: [
        { name: 'limit', type: 'number', description: 'Maximum number of unread notifications to return (default 50, max 200).', required: false },
    ],
    scopes: ['global', 'fallback'],
};

export const markNotificationsReadTool: AgentToolDefinition = {
    name: 'markNotificationsRead',
    displayName: 'Mark notifications read',
    description: 'Mark one or more notifications as read by their eventId (from `listNotifications`). Returns the count of entries actually flipped to read and any eventIds that were not found.',
    parameters: [
        { name: 'eventIds', type: 'array', items: { type: 'string' }, description: 'Notification event ids to mark as read.', required: true },
    ],
    scopes: ['global'],
};

export const getShipmentStatusTool: AgentToolDefinition = {
    name: 'getShipmentStatus',
    displayName: 'Track shipment',
    description: 'Look up the current status, location, and ETA of a shipment by tracking number. Returns a formatted summary plus structured components (currentStatus, deliveryDateMs, origin, destination, trackingHistory). Use when the user asks "where is my package", "has the order shipped", "what\'s the ETA", etc.',
    parameters: [
        { name: 'trackingNumber', type: 'string', description: 'The tracking number from the carrier.', required: true },
        { name: 'provider', type: 'string', description: 'Tracking provider: "easypost" or "seventeenTrack". Defaults to "easypost" when omitted.', required: false },
        { name: 'carrierId', type: 'string', description: 'Optional carrier hint (e.g. "ups", "fedex", "usps", "dhl"). Improves accuracy when the carrier is ambiguous from the tracking number alone.', required: false },
        { name: 'force', type: 'boolean', description: 'Optional. When true, bypasses the cached tracker and forces a fresh fetch from the provider.', required: false },
    ],
    scopes: ['global'],
    requiresToggle: ['signifyAuthorized'],
};

/**
 * The OR-set every ledger tool requires. It only filters the tool surface: the ledger callables' own
 * `assertLedgerRight` on the definition's `readRight`/`postRight`/`commitRight` is the authoritative gate.
 * A ledger whose `access` names a non-ledger right is still gated correctly, but its tools are hidden from
 * users who hold only that right.
 */
export const LEDGER_TOOL_RIGHTS: UserRightId[] = ['ledgerRead', 'ledgerPost', 'ledgerCommit'];

const LEDGER_TOOL_READ_ONLY = 'Amounts are integer minor units of the response\'s `currency`; `counterpartyId` is a record id in the ledger\'s `counterpartyEntityId`. Read-only: it never posts, proposes or commits.';

const ledgerIdParameter: AgentToolParameter = { name: 'ledgerId', type: 'string', description: 'The ledger id, from `listLedgers`.', required: true };

const ledgerLimitParameter: AgentToolParameter = { name: 'limit', type: 'number', description: 'Page size (default 50, max 200).', required: false };

export const listLedgersTool: AgentToolDefinition = {
    name: 'listLedgers',
    displayName: 'List ledgers',
    description: `List the reconciliation ledgers the user may read: each ledger's ledgerId, label, description, currency, counterpartyEntityId, claimKinds, channels, adjustmentReasons and aging buckets. Call it first to find the ledgerId every other ledger tool takes. ${LEDGER_TOOL_READ_ONLY}`,
    parameters: [],
    scopes: ['global'],
    requires: LEDGER_TOOL_RIGHTS,
};

export const listLedgerClaimGroupsTool: AgentToolDefinition = {
    name: 'listLedgerClaimGroups',
    displayName: 'List ledger claim groups',
    description: `List a ledger's claims rolled up by counterparty, or by match group within one counterparty when \`counterpartyId\` is given, with accrued, settled, adjusted and open totals. Page with \`after\` set to the previous response's \`next\`. ${LEDGER_TOOL_READ_ONLY}`,
    parameters: [
        ledgerIdParameter,
        { name: 'counterpartyId', type: 'string', description: 'Optional counterparty record id; groups by match group within it.', required: false },
        { name: 'status', type: 'string', description: '"open" (default) for claims with a non-zero open amount, or "all".', required: false, schema: { type: 'string', enum: ['open', 'all'] } },
        { name: 'after', type: 'string', description: 'The previous page\'s `next`.', required: false },
        ledgerLimitParameter,
    ],
    scopes: ['global'],
    requires: LEDGER_TOOL_RIGHTS,
};

export const listLedgerClaimsTool: AgentToolDefinition = {
    name: 'listLedgerClaims',
    displayName: 'List ledger claims',
    description: `List the claims of one counterparty and match group, each with its totals, claim kind, expected date, terms and exception flags. Page with \`after\` set to the previous response's \`next\`. ${LEDGER_TOOL_READ_ONLY}`,
    parameters: [
        ledgerIdParameter,
        { name: 'counterpartyId', type: 'string', description: 'The counterparty record id.', required: true },
        { name: 'matchGroup', type: 'string', description: 'The match group, from `listLedgerClaimGroups`.', required: true },
        { name: 'status', type: 'string', description: '"open" (default) for claims with a non-zero open amount, or "all".', required: false, schema: { type: 'string', enum: ['open', 'all'] } },
        { name: 'after', type: 'string', description: 'The previous page\'s `next`.', required: false },
        ledgerLimitParameter,
    ],
    scopes: ['global'],
    requires: LEDGER_TOOL_RIGHTS,
};

export const listLedgerPaymentsTool: AgentToolDefinition = {
    name: 'listLedgerPayments',
    displayName: 'List ledger payments',
    description: `List a ledger's payments, newest received first, each with its received, allocated, adjusted and unapplied totals and its remittance lines. Page with \`after\` set to the previous response's \`next\`. ${LEDGER_TOOL_READ_ONLY}`,
    parameters: [
        ledgerIdParameter,
        { name: 'counterpartyId', type: 'string', description: 'Optional counterparty record id.', required: false },
        { name: 'status', type: 'string', description: '"unapplied" (default) for a non-zero unapplied amount, or "all".', required: false, schema: { type: 'string', enum: ['unapplied', 'all'] } },
        {
            name: 'after',
            type: 'object',
            description: 'The previous page\'s `next`, passed back unchanged.',
            required: false,
            schema: { type: 'object', properties: { receivedAt: { type: 'string' }, paymentId: { type: 'string' } }, required: ['receivedAt', 'paymentId'] },
        },
        ledgerLimitParameter,
    ],
    scopes: ['global'],
    requires: LEDGER_TOOL_RIGHTS,
};

export const getLedgerAgingTool: AgentToolDefinition = {
    name: 'getLedgerAging',
    displayName: 'Read ledger aging',
    description: `Read a ledger's aging of open claims: totals per bucket of whole local days past the expected date, and counterparties ranked by the amount past expected. ${LEDGER_TOOL_READ_ONLY}`,
    parameters: [ledgerIdParameter],
    scopes: ['global'],
    requires: LEDGER_TOOL_RIGHTS,
};

export const getLedgerCashFlowForecastTool: AgentToolDefinition = {
    name: 'getLedgerCashFlowForecast',
    displayName: 'Read cash-flow forecast',
    description: `Read a ledger's cash-flow forecast: open claim amounts by the fiscal period of their expected date, plus unscheduled, before the first period and later. ${LEDGER_TOOL_READ_ONLY}`,
    parameters: [
        ledgerIdParameter,
        { name: 'periods', type: 'number', description: 'Number of periods from the current one (default 6, max 24).', required: false },
    ],
    scopes: ['global'],
    requires: LEDGER_TOOL_RIGHTS,
};

export const listLedgerExceptionsTool: AgentToolDefinition = {
    name: 'listLedgerExceptions',
    displayName: 'List ledger exceptions',
    description: `List a ledger's exceptions of one kind: "overPaid" claims (negative open), "late" claims (ordered by days late), "flagged" claims (noTermsRecord, termsConflict or invalidTermsRecord with an open amount), or "unappliedCash" payments older than the ledger's threshold. Every response carries the counts of all four kinds. Page with \`after\` set to the previous response's \`next\`. ${LEDGER_TOOL_READ_ONLY}`,
    parameters: [
        ledgerIdParameter,
        { name: 'exception', type: 'string', description: 'The exception kind.', required: true, schema: { type: 'string', enum: [...LEDGER_EXCEPTION_KINDS] } },
        {
            name: 'after',
            type: 'object',
            description: 'The previous page\'s `next`, passed back unchanged.',
            required: false,
            schema: { type: 'object', properties: { rank: { type: 'number' }, id: { type: 'string' } }, required: ['rank', 'id'] },
        },
        ledgerLimitParameter,
    ],
    scopes: ['global'],
    requires: LEDGER_TOOL_RIGHTS,
};

export const getLedgerAccountHistoryTool: AgentToolDefinition = {
    name: 'getLedgerAccountHistory',
    displayName: 'Read ledger history',
    description: `Read the postings on one payment's unapplied account or one claim's receivable account, in ascending sequence, with the balance after each. Pass exactly one of \`paymentId\` or \`claimId\`. Page with \`afterSeq\` set to the previous response's \`next\`. ${LEDGER_TOOL_READ_ONLY}`,
    parameters: [
        ledgerIdParameter,
        { name: 'paymentId', type: 'string', description: 'The payment id; reads its unapplied cash history.', required: false },
        { name: 'claimId', type: 'string', description: 'The claim id; reads its receivable history.', required: false },
        { name: 'afterSeq', type: 'number', description: 'The previous page\'s `next`.', required: false },
        ledgerLimitParameter,
    ],
    scopes: ['global'],
    requires: LEDGER_TOOL_RIGHTS,
};

const stripEncodedMedia = (text: string): string =>
    text.replace(/data:[^;]+;base64,[A-Za-z0-9+/=]+/g, '');

const BLOCK_TAG_RE = /<\/?(?:p|div|br|tr|td|th|li|h[1-6]|table|thead|tbody|tfoot)(?:\s[^>]*)?\/?>/gi;
const ANY_TAG_RE = /<[^>]*>/g;

const stripHtmlTags = (text: string): string =>
    text
        .replace(BLOCK_TAG_RE, '\n')
        .replace(ANY_TAG_RE, '')
        .replace(/[ \t]+/g, ' ')
        .replace(/\n[ \t]+/g, '\n')
        .replace(/\n{3,}/g, '\n\n')
        .trim();

export function truncateUtf8ByBytes(text: string, maxBytes: number): string {
    const encoder = new TextEncoder();
    if (encoder.encode(text).length <= maxBytes) return text;
    let lo = 0;
    let hi = text.length;
    while (lo < hi) {
        const mid = (lo + hi + 1) >>> 1;
        if (encoder.encode(text.slice(0, mid)).length <= maxBytes) lo = mid;
        else hi = mid - 1;
    }
    return text.slice(0, lo);
}

/**
 * Sanitizes an email body: strips base64-encoded media, strips HTML tags,
 * and truncates the result to `maxBytes` (UTF-8). Returns the cleaned body
 * and a flag indicating whether any modifications occurred.
 */
export const sanitizeEmailBody = (
    rawBody: string | undefined,
    options?: { maxBytes?: number },
): { body: string; wasStripped: boolean } => {
    const maxBytes = options?.maxBytes ?? MAX_COMMUNICATION_LOG_BODY_SIZE;
    if (!rawBody) {
        return { body: '', wasStripped: false };
    }

    const originalLength = rawBody.length;

    const noMedia = stripEncodedMedia(rawBody);
    const noHtml = stripHtmlTags(noMedia);

    const mediaStripped = noMedia.length < rawBody.length;
    const htmlStripped = /<[^>]+>/.test(noMedia) && noHtml !== noMedia;

    const bodyBytes = new TextEncoder().encode(noHtml).length;
    const needsTruncation = bodyBytes > maxBytes;

    const notes: string[] = [];
    if (mediaStripped) notes.push('encoded media removed');
    if (htmlStripped) notes.push('HTML stripped');
    if (needsTruncation) notes.push(`truncated from ${originalLength} chars`);
    const suffix = notes.length > 0 ? `\n\n[Content sanitized: ${notes.join(', ')}]` : '';
    const suffixBytes = new TextEncoder().encode(suffix).length;

    let finalBody = noHtml;
    if (needsTruncation) {
        finalBody = truncateUtf8ByBytes(noHtml, Math.max(0, maxBytes - suffixBytes));
    }

    const wasStripped = mediaStripped || htmlStripped || needsTruncation;
    return { body: `${finalBody}${suffix}`, wasStripped };
};

/**
 * Targets the agent can navigate the user to. Each target except `home` and `forms`/`entities`
 * requires the matching id parameters; the dispatcher rejects calls with missing ids.
 */
export type AgentNavigateTarget =
    | 'home'
    | 'forms'
    | 'form'
    | 'entities'
    | 'entity'
    | 'entityRecord';

/**
 * Resolves a navigate-tool call into the concrete app path. Returns `null` when required
 * ids are missing for the chosen target.
 */
export function resolveNavigatePath(args: {
    target: AgentNavigateTarget;
    formId?: string;
    versionId?: string;
    entityId?: string;
    recordId?: string;
}): string | null {
    switch (args.target) {
        case 'home':
            return '/';
        case 'forms':
            return '/forms';
        case 'form': {
            if (!args.formId) return null;
            if (args.versionId) return `/forms/${args.formId}/formVersions/${args.versionId}`;
            return `/forms/${args.formId}`;
        }
        case 'entities':
            return '/entities';
        case 'entity':
            return args.entityId ? `/entities/${args.entityId}` : null;
        case 'entityRecord':
            return args.entityId && args.recordId
                ? `/entities/${args.entityId}/records/${args.recordId}`
                : null;
        default:
            return null;
    }
}

export const navigateTool: AgentToolDefinition = {
    name: 'navigate',
    displayName: 'Open page',
    description: 'Navigate the user to another page in the app. Use this after creating a form to open it, or to take the user to a specific entity record. The user sees the page change in their browser.',
    parameters: [
        {
            name: 'target',
            type: 'string',
            description: 'One of "home" (/), "forms" (forms list), "form" (a single form — requires formId, optional versionId), "entities" (entities list), "entity" (single entity — requires entityId), or "entityRecord" (single record — requires entityId and recordId).',
            required: true,
        },
        { name: 'formId', type: 'string', description: 'Form document ID. Required when target is "form".', required: false },
        { name: 'versionId', type: 'string', description: 'Form version ID. Optional when target is "form".', required: false },
        { name: 'entityId', type: 'string', description: 'Entity document ID. Required when target is "entity" or "entityRecord".', required: false },
        { name: 'recordId', type: 'string', description: 'Entity record ID. Required when target is "entityRecord".', required: false },
    ],
    scopes: ['global', 'fallback'],
};

export const finishTool: AgentToolDefinition = {
    name: 'finish',
    displayName: 'Finish',
    description: 'Signal that you have completed your objective. You MUST call this when done. Use status "success" when the objective is achieved, "failure" if you cannot make progress, or "partial" if only partially completed.',
    parameters: [
        {
            name: 'status',
            type: 'string',
            description: 'Completion status: "success", "failure", or "partial"',
            required: true,
        },
        {
            name: 'summary',
            type: 'string',
            description: 'A brief summary of what was accomplished or why the objective could not be completed',
            required: true,
        },
    ],
    scopes: ['global', 'fallback'],
};

// ─── Document Write Tools (auto-bound formVersion / record) ──────────────────

export const setFieldTool: AgentToolDefinition = {
    name: 'setField',
    displayName: 'Update field',
    description: 'Write a value to a single field on the bound form version or record. The target document is bound from page context — only `fieldId` and `value` come from the model. Returns `{ ok, fieldId }` on success or a structured rejection on validation failure.',
    parameters: [
        { name: 'fieldId', type: 'string', description: 'The field id to set on the bound document.', required: true },
        { name: 'value', description: 'The value to store. May be a string, number, boolean, or `{ value, description? }` shape.', required: true, type: 'string' },
    ],
    scopes: ['formVersion', 'record'],
    requires: ['formsWrite', 'recordsWrite', 'formsAdmin', 'recordsAdmin', 'orgsAdmin'],
    requiresApproval: ['write'],
    preview: (input: unknown) => {
        const obj = (input ?? {}) as { fieldId?: unknown; value?: unknown };
        const fieldId = typeof obj.fieldId === 'string' ? obj.fieldId : '<field>';
        const raw = (obj.value && typeof obj.value === 'object' && 'value' in (obj.value as Record<string, unknown>))
            ? (obj.value as { value: unknown }).value
            : obj.value;
        const rendered = raw === undefined || raw === null ? 'null' : JSON.stringify(raw);
        const short = rendered.length > 60 ? `${rendered.slice(0, 57)}...` : rendered;
        return { summary: `Set "${fieldId}" to ${short}` };
    },
};

export const setReferenceTool: AgentToolDefinition = {
    name: 'setReference',
    displayName: 'Link record',
    description: 'Set an entity reference slot on the bound form version or record. The slot key is `groupId` when the reference is grouped (filtered subset of an entity per `Entity.recordGroups`), else `entityId`. The harness denormalizes the linked record\'s display name server-side. Returns `{ ok, slot }`.',
    parameters: [
        { name: 'entityId', type: 'string', description: 'The entity document id the reference points at.', required: true },
        { name: 'groupId', type: 'string', description: 'Optional entity-group id. When present, used as the slot key; otherwise the slot key is `entityId`.', required: false },
        { name: 'recordId', type: 'string', description: 'The entity record id to link.', required: true },
    ],
    scopes: ['formVersion', 'record'],
    requires: ['formsWrite', 'recordsWrite', 'formsAdmin', 'recordsAdmin', 'orgsAdmin'],
    requiresApproval: ['write'],
    preview: (input: unknown) => {
        const obj = (input ?? {}) as { entityId?: unknown; groupId?: unknown; recordId?: unknown };
        const slot = typeof obj.groupId === 'string' && obj.groupId.length > 0
            ? obj.groupId
            : typeof obj.entityId === 'string' ? obj.entityId : '<entity>';
        const recordId = typeof obj.recordId === 'string' ? obj.recordId : '<record>';
        return { summary: `Set reference "${slot}" to record ${recordId}` };
    },
};

export const setTableRowTool: AgentToolDefinition = {
    name: 'setTableRow',
    displayName: 'Update table row',
    description: 'Upsert ONE row in a table on the bound form version or record — ONLY for a single row the user explicitly asked to add or edit. NEVER use it for rows that came from a file or extraction: stage those with `proposeImport` and apply via `applyToDocument`, where the user reviews the whole mapping at once. Pass `rowId` to update an existing row; omit it to insert a new one. `value` carries the per-column cell map, e.g. `{ cells: { partNumber: { value: "ABC-123" } } }`. Returns `{ ok, rowId }`.',
    parameters: [
        { name: 'tableId', type: 'string', description: 'The table type id on the bound document.', required: true },
        { name: 'rowId', type: 'string', description: 'Optional existing row id. Omit to create a new row.', required: false },
        { name: 'value', type: 'object', description: 'Row payload. Either `{ cells: { [typeId]: { value, ... } } }` or a flat `{ [typeId]: { value, ... } }` map.', required: true },
    ],
    scopes: ['formVersion', 'record'],
    requires: ['formsWrite', 'recordsWrite', 'formsAdmin', 'recordsAdmin', 'orgsAdmin'],
    requiresApproval: ['write'],
    preview: (input: unknown) => {
        const obj = (input ?? {}) as { tableId?: unknown; rowId?: unknown };
        const tableId = typeof obj.tableId === 'string' ? obj.tableId : '<table>';
        const mode = typeof obj.rowId === 'string' && obj.rowId.length > 0 ? 'Update' : 'Create';
        return { summary: `${mode} row in "${tableId}"` };
    },
};

export const getTableRowsTool: AgentToolDefinition = {
    name: 'getTableRows',
    displayName: 'Read table rows',
    description: 'List the existing rows of a table on the bound form version or record as `{ rowId, cells: { [columnTypeId]: { value } } }[]`. Use this to find the `rowId` of a row you want to patch via `setTableRow`. The bound `formId`/`formVersionId` (form-version page) or `entityId`/`recordId` (record page) come from page context. Returns `{ rows, truncated }`; `truncated` is true when more rows exist than `limit`.',
    parameters: [
        { name: 'tableId', type: 'string', description: 'The table type id on the bound document.', required: true },
        { name: 'limit', type: 'number', description: 'Max rows to return (default 200, max 500).', required: false },
    ],
    scopes: ['formVersion', 'record'],
};

export const getRelationshipTreeTool: AgentToolDefinition = {
    name: 'getRelationshipTree',
    displayName: 'Read relationship tree',
    description: 'Read a relationship-tree field (e.g. a contractor tree) on the bound form version or record. Returns `{ value: { trees, winningRecordIds }, config }`. `trees` is the hierarchy of nodes (each with a stable `key`, an `entityOrGroupId`, and an optional linked `entityRecordId`); `config` declares which entity types are allowed as children of each node, the max depth, and the root entity. Read this first to get node `key`s and to learn what you may add where before calling `editRelationshipTree`.',
    parameters: [
        { name: 'fieldId', type: 'string', description: 'The relationshipTree field id on the bound document.', required: true },
    ],
    scopes: ['formVersion', 'record'],
};

export const editRelationshipTreeTool: AgentToolDefinition = {
    name: 'editRelationshipTree',
    displayName: 'Edit relationship tree',
    description: 'Apply ONE structural edit to a relationship-tree field on the bound form version or record. Read the tree first with `getRelationshipTree` for node `key`s and the allowed-children `config`. The `operation` selects what to do; supply only the parameters that operation needs:\n- `addNode`: add a node. `entityOrGroupId` (required) must be allowed by config. Omit `parentKey` to add a root node; pass `parentKey` to add a child. Optionally pass `entityRecordId` to link a record immediately.\n- `setNodeRecord`: link a record to a node. `key` + `entityRecordId` required.\n- `setRecipientRole`: assign a recipient role on a recipient node (this is "assign user to role"). `key` + `role` (`to`|`cc`|`bcc`) + `enabled` required.\n- `removeNode`: delete a node and its descendants. `key` required.\n- `markWinning`: mark the winning combination ending at `key`. `key` required.\n- `unmarkWinning`: clear the winning combination. No other params.\nReturns `{ ok, operation, value, affectedKey }`.',
    parameters: [
        { name: 'fieldId', type: 'string', description: 'The relationshipTree field id on the bound document.', required: true },
        { name: 'operation', type: 'string', description: 'Which edit to apply.', required: true, schema: { type: 'string', enum: ['addNode', 'setNodeRecord', 'setRecipientRole', 'removeNode', 'markWinning', 'unmarkWinning'] } },
        { name: 'parentKey', type: 'string', description: 'addNode only: the parent node key. Omit to add a root node.', required: false },
        { name: 'key', type: 'string', description: 'The target node key for setNodeRecord / setRecipientRole / removeNode / markWinning.', required: false },
        { name: 'entityOrGroupId', type: 'string', description: 'addNode only: the entity or entity-group id for the new node (must be allowed by config).', required: false },
        { name: 'entityRecordId', type: 'string', description: 'The entity record id to link, for addNode (optional) or setNodeRecord (required).', required: false },
        { name: 'role', type: 'string', description: 'setRecipientRole only: the email role.', required: false, schema: { type: 'string', enum: ['to', 'cc', 'bcc'] } },
        { name: 'enabled', type: 'boolean', description: 'setRecipientRole only: whether the recipient is enabled.', required: false },
    ],
    scopes: ['formVersion', 'record'],
    requires: ['formsWrite', 'recordsWrite', 'formsAdmin', 'recordsAdmin', 'orgsAdmin'],
    requiresApproval: (input: unknown): ApprovalReason[] => {
        const op = (input ?? {}) as { operation?: unknown };
        return op.operation === 'removeNode' || op.operation === 'unmarkWinning' ? ['destructive'] : ['write'];
    },
    preview: (input: unknown) => {
        const obj = (input ?? {}) as { operation?: unknown; fieldId?: unknown };
        const operation = typeof obj.operation === 'string' ? obj.operation : '<op>';
        const fieldId = typeof obj.fieldId === 'string' ? obj.fieldId : '<field>';
        return { summary: `${operation} on tree "${fieldId}"` };
    },
};

export const getCommunicationLogTool: AgentToolDefinition = {
    name: 'getCommunicationLog',
    displayName: 'Read communication log',
    description: 'Read the entries of a communication-log field on the bound form version or record. Returns the most recent non-deleted entries as `{ id, type, timestamp, summary }[]`. Use the `id` to soft-delete an entry via `deleteCommunicationEntry`.',
    parameters: [
        { name: 'fieldId', type: 'string', description: 'The communicationLog field id on the bound document.', required: true },
        { name: 'limit', type: 'number', description: 'Max entries to return (default 200, max 500).', required: false },
    ],
    scopes: ['formVersion', 'record'],
};

export const appendCommunicationEntryTool: AgentToolDefinition = {
    name: 'appendCommunicationEntry',
    displayName: 'Log communication',
    description: 'Append one entry to a communication-log field on the bound form version or record. `type` selects the entry kind; supply only its fields:\n- `phone`: a call. Optional `direction` (`inbound`|`outbound`), `phoneNumber`, `notes`.\n- `attachment`: links an ALREADY-shared file by `fileUid` (required); optional `nextFileName`. Share a file first with `createAttachmentLink`.\n- `email`: email metadata. `from`, `to` (array), `subject` required; optional `cc`, `bcc`, `body`. New file attachments are not supported here.\nServer stamps the id, timestamps, and tag. Returns `{ ok, id, type }`.',
    parameters: [
        { name: 'fieldId', type: 'string', description: 'The communicationLog field id on the bound document.', required: true },
        { name: 'type', type: 'string', description: 'The entry kind.', required: true, schema: { type: 'string', enum: ['phone', 'attachment', 'email'] } },
        { name: 'direction', type: 'string', description: 'phone only: call direction.', required: false, schema: { type: 'string', enum: ['inbound', 'outbound'] } },
        { name: 'phoneNumber', type: 'string', description: 'phone only: the phone number.', required: false },
        { name: 'notes', type: 'string', description: 'phone only: free-text notes about the call.', required: false },
        { name: 'fileUid', type: 'string', description: 'attachment only (required for attachment): the uid of an already-shared file.', required: false },
        { name: 'nextFileName', type: 'string', description: 'attachment only: display name for the file.', required: false },
        { name: 'from', type: 'string', description: 'email only: sender address.', required: false },
        { name: 'to', type: 'array', items: { type: 'string' }, description: 'email only: recipient addresses.', required: false },
        { name: 'cc', type: 'array', items: { type: 'string' }, description: 'email only: cc addresses.', required: false },
        { name: 'bcc', type: 'array', items: { type: 'string' }, description: 'email only: bcc addresses.', required: false },
        { name: 'subject', type: 'string', description: 'email only: subject line.', required: false },
        { name: 'body', type: 'string', description: 'email only: body text.', required: false },
        { name: 'contactEntityRecordId', type: 'string', description: 'Optional: the contact record this communication is with.', required: false },
    ],
    scopes: ['formVersion', 'record'],
    requires: ['formsWrite', 'recordsWrite', 'formsAdmin', 'recordsAdmin', 'orgsAdmin'],
    requiresApproval: ['write'],
    preview: (input: unknown) => {
        const obj = (input ?? {}) as { type?: unknown; fieldId?: unknown };
        const type = typeof obj.type === 'string' ? obj.type : '<type>';
        const fieldId = typeof obj.fieldId === 'string' ? obj.fieldId : '<field>';
        return { summary: `Log ${type} entry on "${fieldId}"` };
    },
};

export const deleteCommunicationEntryTool: AgentToolDefinition = {
    name: 'deleteCommunicationEntry',
    displayName: 'Delete communication entry',
    description: 'Soft-delete one entry in a communication-log field on the bound form version or record. The entry stays in storage flagged deleted and is hidden from reads. Get the `entryId` from `getCommunicationLog`. Returns `{ ok, id }`.',
    parameters: [
        { name: 'fieldId', type: 'string', description: 'The communicationLog field id on the bound document.', required: true },
        { name: 'entryId', type: 'string', description: 'The id of the entry to delete (from getCommunicationLog).', required: true },
    ],
    scopes: ['formVersion', 'record'],
    requires: ['formsWrite', 'recordsWrite', 'formsAdmin', 'recordsAdmin', 'orgsAdmin'],
    requiresApproval: ['destructive'],
    preview: (input: unknown) => {
        const obj = (input ?? {}) as { entryId?: unknown; fieldId?: unknown };
        const entryId = typeof obj.entryId === 'string' ? obj.entryId : '<entry>';
        const fieldId = typeof obj.fieldId === 'string' ? obj.fieldId : '<field>';
        return { summary: `Delete entry ${entryId} from "${fieldId}"` };
    },
};

export const applyToDocumentTool: AgentToolDefinition = {
    name: 'applyToDocument',
    displayName: 'Fill form from document',
    description: 'Apply a staged extraction (`processIndex` from `proposeImport` on this formVersion/record page) onto the bound document — a form version OR an entity record — in ONE approved step. The staged process document is read server-side and applied in full: header fields, entity references, and every assigned table\'s rows (each row carrying its Typesafe value/summary/junk tag — nothing is dropped). You pass only the pointers; the values, mappings, and scores come from the staged document, and the user\'s Mapping Review edits (remaps, overrides, rejections) are honored at write. Pass `useResolutions: true` (after `findPartsBatch`) to overlay the richer parts resolutions on top of the staged reference resolutions. The bound `formId`/`formVersionId` (form-version page) or `entityId`/`recordId` (record page) come from page context — not inputs. Returns `{ ok, fields, references, tables }` write reports — inspect the rejections.',
    parameters: [
        { name: 'fileId', type: 'string', description: 'The file\'s durable handle, surfaced as `<file_id>` (from `extractFromFile`).', required: true },
        { name: 'processIndex', type: 'number', description: 'Index (n) of the process doc at `<fileFolder>/process/<n>.json` from `proposeImport` on this page.', required: true },
        { name: 'useResolutions', type: 'boolean', description: 'Optional. Set true to read `<fileFolder>/partResolutions.json` written by `findPartsBatch`.', required: false },
    ],
    scopes: ['formVersion', 'record'],
    requires: ['formsWrite', 'recordsWrite', 'formsAdmin', 'recordsAdmin', 'orgsAdmin'],
    requiresApproval: ['bulk'],
    preview: (input: unknown) => {
        const obj = (input ?? {}) as { processIndex?: unknown };
        const idx = typeof obj.processIndex === 'number' ? ` ${obj.processIndex}` : '';
        return { summary: `Apply staged document${idx} to this form` };
    },
};

export const deleteFieldTool: AgentToolDefinition = {
    name: 'deleteField',
    displayName: 'Clear field',
    description: 'Clear the value of a single field on the bound form version or record. Returns `{ ok, fieldId }` or a structured rejection.',
    parameters: [
        { name: 'fieldId', type: 'string', description: 'The field id to clear on the bound document.', required: true },
    ],
    scopes: ['formVersion', 'record'],
    requires: ['formsWrite', 'recordsWrite', 'formsAdmin', 'recordsAdmin', 'orgsAdmin'],
    requiresApproval: ['destructive'],
    preview: (input: unknown) => {
        const obj = (input ?? {}) as { fieldId?: unknown };
        const fieldId = typeof obj.fieldId === 'string' ? obj.fieldId : '<field>';
        return { summary: `Clear field "${fieldId}"` };
    },
};

export const deleteReferenceTool: AgentToolDefinition = {
    name: 'deleteReference',
    displayName: 'Unlink record',
    description: 'Clear an entity reference slot on the bound form version or record. The slot key is `groupId` when grouped, else `entityId`. Does NOT delete members of a group — only the reference slot. Returns `{ ok, slot }`.',
    parameters: [
        { name: 'entityId', type: 'string', description: 'The entity document id of the slot to clear (used as slot key when `groupId` is omitted).', required: true },
        { name: 'groupId', type: 'string', description: 'Optional entity-group id. When present, used as the slot key.', required: false },
    ],
    scopes: ['formVersion', 'record'],
    requires: ['formsWrite', 'recordsWrite', 'formsAdmin', 'recordsAdmin', 'orgsAdmin'],
    requiresApproval: ['destructive'],
    preview: (input: unknown) => {
        const obj = (input ?? {}) as { entityId?: unknown; groupId?: unknown };
        const slot = typeof obj.groupId === 'string' && obj.groupId.length > 0
            ? obj.groupId
            : typeof obj.entityId === 'string' ? obj.entityId : '<entity>';
        return { summary: `Clear reference "${slot}"` };
    },
};

export const reorderTableRowsTool: AgentToolDefinition = {
    name: 'reorderTableRows',
    displayName: 'Reorder table rows',
    description: 'Set the explicit row order for a table on the bound form version or record. `orderedRowIds` is the complete ordering of all rows the agent wants to position. Rows not in the list keep their relative order behind the listed ones. Returns `{ ok, reorderedCount }`.',
    parameters: [
        { name: 'tableId', type: 'string', description: 'The table type id on the bound document.', required: true },
        { name: 'orderedRowIds', type: 'array', items: { type: 'string' }, description: 'Row ids in their new desired order.', required: true },
    ],
    scopes: ['formVersion', 'record'],
    requires: ['formsWrite', 'recordsWrite', 'formsAdmin', 'recordsAdmin', 'orgsAdmin'],
    requiresApproval: (input: unknown): ApprovalReason[] => {
        const obj = (input ?? {}) as { orderedRowIds?: unknown };
        return Array.isArray(obj.orderedRowIds) && obj.orderedRowIds.length > 3 ? ['bulk'] : ['write'];
    },
    preview: (input: unknown) => {
        const obj = (input ?? {}) as { tableId?: unknown; orderedRowIds?: unknown };
        const tableId = typeof obj.tableId === 'string' ? obj.tableId : '<table>';
        const count = Array.isArray(obj.orderedRowIds) ? obj.orderedRowIds.length : 0;
        return { summary: `Reorder ${count} row${count === 1 ? '' : 's'} in "${tableId}"`, counts: { rows: count } };
    },
};

export const clearTableTool: AgentToolDefinition = {
    name: 'clearTable',
    displayName: 'Clear table',
    description: 'Remove every row from a table on the bound form version or record. Keeps the table schema; rows only. Returns `{ ok, deletedCount }`.',
    parameters: [
        { name: 'tableId', type: 'string', description: 'The table type id on the bound document.', required: true },
    ],
    scopes: ['formVersion', 'record'],
    requires: ['formsWrite', 'recordsWrite', 'formsAdmin', 'recordsAdmin', 'orgsAdmin'],
    requiresApproval: ['destructive', 'bulk'],
    preview: (input: unknown) => {
        const obj = (input ?? {}) as { tableId?: unknown };
        const tableId = typeof obj.tableId === 'string' ? obj.tableId : '<table>';
        return { summary: `Clear all rows from "${tableId}"` };
    },
};

export const listAttachmentsTool: AgentToolDefinition = {
    name: 'listAttachments',
    displayName: 'List attachments',
    description: 'List attachment links on the bound form version or record. Returns `{ attachments: [{ attachmentId, fileName?, currentVersionId? }, ...] }` — file storage paths are NOT returned (use `createAttachmentLink` to share a file).',
    parameters: [],
    scopes: ['formVersion', 'record'],
};

export const setArchiveStateTool: AgentToolDefinition = {
    name: 'setArchiveState',
    displayName: 'Archive form',
    description: 'Archive or unarchive the bound form. Boolean toggle: `archived: true` moves the form to the archive collection; `archived: false` restores it. Runs the real archive procedure (moves form + every version). Returns `{ ok, archived }`.',
    parameters: [
        { name: 'archived', type: 'boolean', description: 'True to archive, false to unarchive.', required: true },
    ],
    scopes: ['formVersion'],
    requires: ['formsArchive', 'formsAdmin', 'orgsAdmin'],
    requiresApproval: ['destructive'],
    preview: (input: unknown) => {
        const obj = (input ?? {}) as { archived?: unknown };
        return { summary: obj.archived === true ? 'Archive this form' : 'Unarchive this form' };
    },
};

// ─── Identity, listings, lifecycle (records-tools lane) ──────────────────────

export const whoamiTool: AgentToolDefinition = {
    name: 'whoami',
    displayName: 'Check my access',
    description: 'Identity + rights + toggle snapshot for the current session. Returns `{ uid, userRights, toggles }`. Use to discover which tools you can run before proposing a write.',
    parameters: [],
    scopes: ['global', 'fallback'],
};

export const runNamedAnalyticsQueryTool: AgentToolDefinition = {
    name: 'runNamedAnalyticsQuery',
    displayName: 'Query analytics',
    description:
        'Answer a fixed analytics question for the signed-in user. `query`: `salesClosedTotal` returns one `{ amount, creditAmount }`: `amount` is booked sales, the whole ordered amount of every order line whose status is a released status and whose release date is in the period, each line once however many people it credits; `creditAmount` is sales credit, each credited salesperson\'s share of those lines (the line amount times their weight), and a line credited to nobody adds nothing to it (use `amount` for "what did we book this year?" and `creditAmount` for "what is my credit toward my goal?"; at `level` `self` both cover only the lines credited to the caller, and the goal compares with `creditAmount`); `salesClosedByMonth` returns `{ month, amount, creditAmount }` per month of the release date; `salesClosedByPerson` and `salesClosedByTeam` return one row per person or team with `amount`, the whole amount of the lines credited to them, and `creditAmount`, their credit; a line credited to two people counts in full in each person\'s `amount`, so never add `amount` across rows; the row with a null salesperson holds the lines credited to nobody, and its `creditAmount` is 0; a salesperson in several teams counts in full in each of their team rows, in `salesClosedByTeam` and in the series at `parameters.group` `team` alike, so team rows are never added together, and they may add up to more than the Org total; `salesClosedByTeam` rows also carry `teamSalespeople`, the distinct salespeople credited in the team, and `totalSalespeople`, the distinct salespeople credited across the whole result, the same on every row; a salesperson credited in two teams counts once in `totalSalespeople`, so never add `teamSalespeople` across teams; `bookedSeries`, `ordersWrittenSeries` and `quotesIssuedSeries` return one row per series line, at most 11: `bookedSeries` is dated by the release date and sums the whole amount of released order lines and counts those lines, each line once per row, and `ordersWrittenSeries` and `quotesIssuedSeries` are dated by the sales credit date and count the distinct forms with a credit row at an order stage and at a quote stage; each row is `{ groupKey, groupRank, total, bucketStarts, values, counts, totalGroups }`, where `bucketStarts`, `values` (decimal strings) and `counts` are JSON lists with one entry per bucket of `parameters.grain`, zero where nothing happened, and `total` is the whole period of the line; `parameters.group` `all` returns one row with a null `groupKey` at `groupRank` 1, and `salesperson` or `team` return the top 10 groups by `total` at ranks 1 to 10 (a null `groupKey` there is unassigned) plus, when `totalGroups` is over 10, one row at `groupRank` 11 with a null `groupKey` that combines all the other groups; a line or form credited to two people counts once in each of their rows, so never add values or counts across rows; `quoteCountAndValueByKind` returns one `{ kind, quotes, quotedAmount }` row each for `lighting` and `controls` quotes (a quote with lines of both kinds counts in each, so never add the two rows); `quoteValueTotal` returns one row per stage list, `{ stages, quotedAmount }`; never add them without saying so; `releaseValueByState` returns one `{ state, orderedAmount }` row each for `held` and `released` order line items; the `released` row is order lines whose status is released or later (the configured released list), and never cancelled; `orderLinesTotals` returns one row of order line totals: `openLines` and `openForms` (lines without a fulfilled status and their distinct forms), `heldLines`, `releasedLines` and `shippedLines` with their values, `pastPromisedLines` with `valueAtRisk`, and `shippedNotInvoicedValue`; a shipped line counts in both `releasedLines` and `shippedLines`, so the two are never added; each value is the credited share of the caller, and its `line`-prefixed twin (such as `lineHeldValue`) the whole line amount; `orderLinesList` returns one row per order line, at most 500: `docPath`, `formId`, `projectId`, `lineItemStatus`, `orderedAmount` (the credited share) beside `lineOrderedAmount` (the whole line), `promisedDate`, `pastPromised`, the form, project and item of the line as `formNumber`, `projectName`, `itemType`, `itemDescription`, `manufacturerName`, `quantity`, and `totalLines`, every line before the cap; `invoiceList` returns one row per invoice, at most 500: `formId`, `invoiceNumber`, `projectId`, `projectName`, `invoiceDate`, `invoiceDueDate`, `invoiceStatus`, `bucket`, `invoicedAmount` and `outstandingAmount` (the credited share) beside `lineInvoicedAmount` and `lineOutstandingAmount` (the whole invoice), and `totalInvoices`, every invoice before the cap; `openQuotesAndBuysByProject` returns `{ projectId, stages, forms, quotedAmount }` rows, the quotes (`stages` is `quote`) and buys (`buy`) that no order has closed yet, per project, whatever their date unless open items are configured `inPeriod`; `conversionByKind` returns one `{ kind, quotes, converted }` row each for `lighting` and `controls`: the quotes of the period and how many an order has converted, and the conversion rate is `converted / quotes`; `projectsByStage` returns one row per active project, at most 500: a project at an open stage or `InDesign` (no form yet); Complete and Lost projects are left out. Rows are ordered by `value` descending, missing values last, and carry `projectId`, `projectName`, `stage`, `salespersonMemberIds` and `teamIds` (JSON lists), `stageLists` (a JSON list of the stage lists the project has forms in), `forms`, `value` (the open value at the highest open stage of the project, null for `InDesign`), `updatedAt` (the creation time of the latest form version), `invoicedAmount`, `outstandingAmount`, `outstandingInvoices` and `oldestOutstandingDueDate` over the order forms of the project with no invoice-date filter, `formList` (a JSON list of its newest forms, at most 20, each `{ formId, formNumber, stage, state, updatedAt }`) with `formValues` (a JSON list of `{ formId, value }` for the same forms in the same order, refused alone when values are not configured) beside `formListTotal`, every form, and `totalProjects`, every active project before the cap, with `totalValue`, the summed `value` of those projects, the same on every row; each row is valued at one stage, so never add `value` across projects without saying so; `pipelineByStage` returns one `{ stage, released, forms, projects, value }` row per form type as stored (`stage`) among the forms the caller can see, whatever their date unless open items are configured `inPeriod`: `value` is the whole amount of its forms\' lines (quoted for quote and buy types, ordered otherwise), `forms` its distinct forms and `projects` the distinct projects those forms belong to (a form with no project adds to `forms` and `value` only); an order type has one row with `released` true and one with `released` false, its lines split by line status, so one order can count in both; every other type has one row with `released` null; a project with forms of several types counts under each type, so never add rows across types; `overdueInvoicesBySummaryScope` returns `{ groupingSet, salespersonMemberId, overdueInvoices }` rows, salesperson and total only: the distinct invoices more than 60 days past due per salesperson (`groupingSet` is `salesperson`) and in total (`total`); an invoice credited to two people counts once in the row of each person and once in the total, so never add the rows, and read the total from the `total` row, never from a row whose salesperson is null. `parameters.period`: `ytd`, `qtd`, `mtd` or `wtd`, each to date, or `lastWeek` or `lastMonth`, the previous whole week or month, in the configured fiscal calendar. `parameters.grain` (`day`, `week`, `month` or `quarter`) and `parameters.group` (`all`, `salesperson` or `team`) are required for the series queries and refused on any other query; leave them out everywhere else. `level`: `self` (My sales), `team` (My team) or `org` (Org, needs metricsOrgRead or metricsAdmin). Returns `{ query, parameters, level, period: { from, to, label }, snapshotId, builds, refusedColumns, rows }`; `level` is the level granted. When every row does not fit in one tool result, `rows` holds only the leading rows that do, and the result adds `rowsTruncated: true` and `totalRows`, the row count before that cap: say the list is partial, give `totalRows`, and never total or count the returned rows as if they were all of them. Each `refusedColumns` entry `{ column, refusal: "notConfigured", key }` names a column that is null in every row because the configuration binding `key` is missing: say that value is not configured, never that it is zero. Report the numbers in `rows` exactly as returned.',
    parameters: [
        { name: 'query', type: 'string', description: 'The named query to run.', required: true, schema: { type: 'string', enum: [...NAMED_QUERY_IDS] } },
        {
            name: 'parameters',
            type: 'object',
            description: 'The query parameters, each one of its declared values.',
            required: true,
            schema: {
                type: 'object',
                properties: {
                    period: { type: 'string', enum: [...NAMED_QUERY_PARAMETER_ENUMS.period] },
                    ...Object.fromEntries(
                        Object.entries(NAMED_QUERY_SERIES_PARAMETER_ENUMS).map(([name, values]) => [
                            name,
                            { type: 'string', enum: [...values], description: 'Required for the series queries `bookedSeries`, `ordersWrittenSeries` and `quotesIssuedSeries`; refused on any other query.' },
                        ]),
                    ),
                },
                required: Object.keys(NAMED_QUERY_PARAMETER_ENUMS),
                additionalProperties: false,
            },
        },
        { name: 'level', type: 'string', description: 'The scope to answer at.', required: true, schema: { type: 'string', enum: [...ANALYTICS_SCOPE_LEVELS] } },
    ],
    additionalProperties: false,
    scopes: ['global'],
};

export const listEntitiesTool: AgentToolDefinition = {
    name: 'listEntities',
    displayName: 'List entities',
    description: 'List the entity definitions in the tenant. Returns `{ entities: [{ entityId, name?, status }] }`. Use before `getEntity` or before navigating to an entity page.',
    parameters: [],
    scopes: ['global', 'fallback'],
};

export const getEntityTool: AgentToolDefinition = {
    name: 'getEntity',
    displayName: 'Read entity',
    description: 'Read an entity definition. Returns `{ entity, schema: { fieldIds, entityReferences } }`. Use to discover the field surface of an entity before creating or filtering records.',
    parameters: [
        { name: 'entityId', type: 'string', description: 'The entity document id.', required: true },
    ],
    scopes: ['global', 'fallback'],
};

export const listRecordsTool: AgentToolDefinition = {
    name: 'listRecords',
    displayName: 'List records',
    description: 'Paginated, deterministic listing of records under the bound entity. `entityId` is auto-bound. Returns `{ records: [{ recordId, name?, status }], cursor? }`. Optional `status` filter restricts to Active / Inactive / Pending. Pass the returned `cursor` to get the next page.',
    parameters: [
        { name: 'cursor', type: 'string', description: 'Cursor returned by the previous page; omit on the first call.', required: false },
        { name: 'pageSize', type: 'number', description: 'Records per page (1-200, default 50).', required: false },
        { name: 'status', type: 'string', description: 'Optional status filter: "Active", "Inactive", or "Pending".', required: false },
    ],
    scopes: ['entity'],
};

export const setRecordNameTool: AgentToolDefinition = {
    name: 'setRecordName',
    displayName: 'Rename record',
    description: 'Rename the bound entity record. Auto-bound `entityId` + `recordId`. Returns `{ ok, entityId, recordId, name }`.',
    parameters: [
        { name: 'name', type: 'string', description: 'New display name for the record.', required: true },
    ],
    scopes: ['record'],
    requires: ['recordsWrite', 'recordsAdmin', 'orgsAdmin'],
    requiresApproval: ['write'],
    preview: (input: unknown) => {
        const obj = (input ?? {}) as { name?: unknown };
        return { summary: `Rename record to "${typeof obj.name === 'string' ? obj.name : ''}"` };
    },
};

export const setRecordStatusTool: AgentToolDefinition = {
    name: 'setRecordStatus',
    displayName: 'Change record status',
    description: 'Set the bound record\'s status. Auto-bound `entityId` + `recordId`. Restricted to AgentRecordStatus ("Active" | "Inactive" | "Pending") — "Archived" is reserved and never accepted. Returns `{ ok, entityId, recordId, status }`.',
    parameters: [
        { name: 'status', type: 'string', description: 'One of "Active", "Inactive", or "Pending".', required: true },
    ],
    scopes: ['record'],
    requires: ['recordsWrite', 'recordsAdmin', 'orgsAdmin'],
    requiresApproval: ['write'],
    preview: (input: unknown) => {
        const obj = (input ?? {}) as { status?: unknown };
        return { summary: `Set status to ${typeof obj.status === 'string' ? obj.status : ''}` };
    },
};

export const setRecordsStatusTool: AgentToolDefinition = {
    name: 'setRecordsStatus',
    displayName: 'Change status of records',
    description: 'Bulk status change for selected records of the bound entity. `entityId` is auto-bound. Restricted to AgentRecordStatus ("Active" | "Inactive" | "Pending") — "Archived" is never accepted. Returns `{ ok, entityId, updated, notFound[] }`.',
    parameters: [
        { name: 'recordIds', type: 'array', items: { type: 'string' }, description: 'Record ids to update.', required: true },
        { name: 'status', type: 'string', description: 'One of "Active", "Inactive", or "Pending".', required: true },
    ],
    scopes: ['entity'],
    requires: ['recordsWrite', 'recordsAdmin', 'orgsAdmin'],
    requiresApproval: ['bulk'],
    preview: (input: unknown) => {
        const obj = (input ?? {}) as { recordIds?: unknown; status?: unknown };
        const count = Array.isArray(obj.recordIds) ? obj.recordIds.length : 0;
        const status = typeof obj.status === 'string' ? obj.status : '';
        return {
            summary: `Set status of ${count} record${count === 1 ? '' : 's'} to ${status}`,
            counts: { records: count },
        };
    },
};

export const listFormVersionsTool: AgentToolDefinition = {
    name: 'listFormVersions',
    displayName: 'List form versions',
    description: 'List every version of the bound form. `formId` is auto-bound. Returns slim summaries ordered by lastModified descending: `{ versions: [{ formVersionId, state?, lastModified?, isCurrent? }] }`.',
    parameters: [],
    scopes: ['formVersion'],
};

export const listVersionsByFormsTool: AgentToolDefinition = {
    name: 'listVersionsByForms',
    displayName: 'List versions for forms',
    description: 'List versions across several forms by id (no auto-binding — supply the ids you want). Returns `{ versionsByFormId: { [formId]: [{ formVersionId, state?, lastModified?, isCurrent? }] } }`.',
    parameters: [
        { name: 'formIds', type: 'array', items: { type: 'string' }, description: 'Form document ids to look up.', required: true },
    ],
    scopes: ['forms', 'home'],
};

export const deleteFormVersionTool: AgentToolDefinition = {
    name: 'deleteFormVersion',
    displayName: 'Delete form version',
    description: 'Delete the bound non-current draft version. Auto-bound `formId` + `formVersionId`. Rejects when the bound version is the form\'s current version. Returns `{ ok, formId, formVersionId }` or `{ ok: false, error }`.',
    parameters: [],
    scopes: ['formVersion'],
    requires: ['formsArchive', 'formsAdmin', 'orgsAdmin'],
    requiresApproval: ['destructive'],
    preview: () => ({ summary: 'Delete this draft version' }),
};

export const archiveFormsTool: AgentToolDefinition = {
    name: 'archiveForms',
    displayName: 'Archive forms',
    description: 'Bulk archive / unarchive forms by id from the list view. Returns `{ ok, requested, succeeded, failures: [{ formId, error }] }`.',
    parameters: [
        { name: 'formIds', type: 'array', items: { type: 'string' }, description: 'Form document ids.', required: true },
        { name: 'archived', type: 'boolean', description: 'True to archive, false to unarchive.', required: true },
    ],
    scopes: ['forms', 'home'],
    requires: ['formsArchive', 'formsAdmin', 'orgsAdmin'],
    requiresApproval: ['destructive', 'bulk'],
    preview: (input: unknown) => {
        const obj = (input ?? {}) as { formIds?: unknown; archived?: unknown };
        const count = Array.isArray(obj.formIds) ? obj.formIds.length : 0;
        const verb = obj.archived === true ? 'Archive' : 'Unarchive';
        return { summary: `${verb} ${count} form${count === 1 ? '' : 's'}`, counts: { forms: count } };
    },
};

export const duplicateWorkflowTool: AgentToolDefinition = {
    name: 'duplicateWorkflow',
    displayName: 'Copy workflow',
    description: 'Duplicate a workflow definition under a new id. The new workflow keeps the source\'s actions / triggers / parameters; the description label gets a "(Copy)" suffix. Returns `{ ok, newWorkflowId }`.',
    parameters: [
        { name: 'workflowId', type: 'string', description: 'The source workflow document id.', required: true },
    ],
    scopes: ['settingsWorkflows'],
    requires: ['workflowsAdmin', 'formsAdmin', 'orgsAdmin'],
    requiresApproval: ['permanent'],
    preview: () => ({ summary: 'Duplicate this workflow' }),
};

// ─── Tool Registry ───────────────────────────────────────────────────────────

export const AGENT_TOOL_REGISTRY = {
    listWorkflows: listWorkflowsTool,
    listForms: listFormsTool,
    getForm: getFormTool,
    duplicateRows: duplicateRowsTool,
    getWorkflow: getWorkflowTool,
    createWorkflow: createWorkflowTool,
    updateWorkflow: updateWorkflowTool,
    deleteWorkflow: deleteWorkflowTool,
    setFormState: setFormStateTool,
    executeWorkflow: executeWorkflowTool,
    extractFromFile: extractFromFileTool,
    startPdfIntake: startPdfIntakeTool,
    readExtractedFile: readExtractedFileTool,
    getSchema: getSchemaTool,
    getFieldDetails: getFieldDetailsTool,
    createForm: createFormTool,
    proposeImport: proposeImportTool,
    applyToRecords: applyToRecordsTool,
    search: searchTool,
    notifyUser: notifyUserTool,
    sendEmail: sendEmailTool,
    computeAggregations: computeAggregationsTool,
    deleteTableRows: deleteTableRowsTool,
    exportTableCsv: exportTableCsvTool,
    createAttachmentLink: createAttachmentLinkTool,
    revokeAttachmentLink: revokeAttachmentLinkTool,
    createFormVersion: createFormVersionTool,
    setCurrentFormVersion: setCurrentFormVersionTool,
    duplicateForm: duplicateFormTool,
    createRecord: createRecordTool,
    getRecord: getRecordTool,
    deleteRecord: deleteRecordTool,
    listNotifications: listNotificationsTool,
    markNotificationsRead: markNotificationsReadTool,
    findParts: findPartsTool,
    findPartEquivalents: findPartEquivalentsTool,
    findPartsBatch: findPartsBatchTool,
    getSignifyProductPriceAndAvailability: getSignifyProductPriceAndAvailabilityTool,
    searchSignifyCustomers: searchSignifyCustomersTool,
    searchSignifyDistributors: searchSignifyDistributorsTool,
    getShipmentStatus: getShipmentStatusTool,
    listLedgers: listLedgersTool,
    listLedgerClaimGroups: listLedgerClaimGroupsTool,
    listLedgerClaims: listLedgerClaimsTool,
    listLedgerPayments: listLedgerPaymentsTool,
    getLedgerAging: getLedgerAgingTool,
    getLedgerCashFlowForecast: getLedgerCashFlowForecastTool,
    listLedgerExceptions: listLedgerExceptionsTool,
    getLedgerAccountHistory: getLedgerAccountHistoryTool,
    navigate: navigateTool,
    finish: finishTool,
    setField: setFieldTool,
    setReference: setReferenceTool,
    setTableRow: setTableRowTool,
    getTableRows: getTableRowsTool,
    getRelationshipTree: getRelationshipTreeTool,
    editRelationshipTree: editRelationshipTreeTool,
    getCommunicationLog: getCommunicationLogTool,
    appendCommunicationEntry: appendCommunicationEntryTool,
    deleteCommunicationEntry: deleteCommunicationEntryTool,
    applyToDocument: applyToDocumentTool,
    deleteField: deleteFieldTool,
    deleteReference: deleteReferenceTool,
    reorderTableRows: reorderTableRowsTool,
    clearTable: clearTableTool,
    listAttachments: listAttachmentsTool,
    setArchiveState: setArchiveStateTool,
    whoami: whoamiTool,
    runNamedAnalyticsQuery: runNamedAnalyticsQueryTool,
    listEntities: listEntitiesTool,
    getEntity: getEntityTool,
    listRecords: listRecordsTool,
    setRecordName: setRecordNameTool,
    setRecordStatus: setRecordStatusTool,
    setRecordsStatus: setRecordsStatusTool,
    listFormVersions: listFormVersionsTool,
    listVersionsByForms: listVersionsByFormsTool,
    deleteFormVersion: deleteFormVersionTool,
    archiveForms: archiveFormsTool,
    duplicateWorkflow: duplicateWorkflowTool,
} as const satisfies Record<string, AgentToolDefinition>;

export type AgentToolName = keyof typeof AGENT_TOOL_REGISTRY;

/** Maps a tool's wire name to its user-facing label, falling back to the raw name for unknown tools. */
export function toolDisplayName(toolName: string): string {
    return toolName in AGENT_TOOL_REGISTRY
        ? AGENT_TOOL_REGISTRY[toolName as AgentToolName].displayName
        : toolName;
}

// ─── Email Attachment Rules ──────────────────────────────────────────────────

export type EmailAttachmentDisposition = 'upload' | 'skip' | 'log';

export type EmailAttachmentRule = {
  contentTypePatterns: string[];
  disposition: EmailAttachmentDisposition;
  minSizeBytes?: number;
  maxSizeBytes?: number;
  parse?: boolean;
  chunkTypes?: string[];
};

/**
 * Maximum number of attachments accepted from a single inbound email during
 * server-side ingestion (`onEmailReceived`, `onDroppedEmail`). Excess
 * attachments are dropped after this count is reached.
 */
export const MAX_EMAIL_ATTACHMENTS_PER_EMAIL = 20;

/**
 * Maximum combined byte size of attachments accepted from a single inbound
 * email during server-side ingestion. Aligned with the `temp/` Storage rule
 * cap (500 MB) so the ingestion pipeline cannot exceed what the bucket allows.
 */
export const MAX_EMAIL_ATTACHMENT_TOTAL_BYTES = 524_288_000;

/**
 * Maximum characters in a single user-typed prompt sent to the agent.
 * Bounds the chat-message payload distinct from the 500 MB Storage rule, which
 * gates files dropped for tool processing (extractFromFile) — those
 * never travel through the LLM request body.
 */
export const MAX_AGENT_PROMPT_CHARS = 8_000;

/**
 * Maximum email body length (in characters) inlined into the agent's prompt.
 * Aligned with MAX_AGENT_PROMPT_CHARS so the body never gets re-truncated
 * server-side. Distinct from MAX_COMMUNICATION_LOG_BODY_SIZE (bytes, Firestore
 * storage cap).
 */
export const MAX_AGENT_EMAIL_BODY_CHARS = 8_000;

/** Maximum number of prior chat turns sent to the model as conversation history. */
export const MAX_AGENT_HISTORY_MESSAGES = 50;

/** Maximum characters per history message after attachment-prefix expansion. */
export const MAX_AGENT_HISTORY_CHARS_PER_MESSAGE = 8_000;

/** Maximum size of a single image attachment in bytes. Matches Anthropic's per-image cap. */
export const MAX_AGENT_IMAGE_BYTES = 5 * 1024 * 1024;

/** Maximum number of image attachments inlined as vision blocks in one agent message. */
export const MAX_AGENT_IMAGES_PER_MESSAGE = 10;

/** Maximum combined size of all image attachments in one agent message. */
export const MAX_AGENT_IMAGE_TOTAL_BYTES = 20 * 1024 * 1024;

/**
 * File extensions routed through the tabular path: parsed by `extractFromFile`,
 * available to the agent as a `<storage_path>` reference (no inline content).
 */
export const AGENT_TABULAR_EXTENSIONS = ['pdf', 'csv', 'json', 'xlsx', 'xml', 'txt'] as const;

/**
 * File extensions routed through the email path: parsed client-side via
 * PostalMime / msgreader; body is inlined into the prompt and child
 * attachments are uploaded as separate temp-inventory entries.
 */
export const AGENT_EMAIL_EXTENSIONS = ['eml', 'msg'] as const;

/**
 * File extensions routed through the image path: uploaded as-is and inlined
 * as native vision content blocks on the first user message.
 */
export const AGENT_IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'gif', 'webp'] as const;

/**
 * IANA media-type allowlist for image attachments — must match what every
 * vision-capable provider (Anthropic, OpenAI, Gemini) accepts. Enforced
 * server-side in `sanitizeImageRefs`.
 */
export const AGENT_IMAGE_MEDIA_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'] as const;

/**
 * Comma-joined `accept` attribute for the agent's file-picker `<input>`.
 * Hint only — backend re-validates everything.
 */
export const AGENT_FILE_ACCEPT_ATTR = [
    ...AGENT_TABULAR_EXTENSIONS,
    ...AGENT_EMAIL_EXTENSIONS,
    ...AGENT_IMAGE_EXTENSIONS,
]
    .map((e) => `.${e}`)
    .join(',');

// ─── AI Email Analysis Parameters ────────────────────────────────────────────

export type EmailExtractionColumnConfig = {
  typeId: TypeId;
  label: string;
  dataType: 'text' | 'number';
};

export type EmailExtractionEntityConfig = {
  entityId: EntityId;
  entityGroupId?: string;
};

/** Firestore `entityReferences` key and extraction JSON key: group id when scoped, else entity id. */
export function entityReferenceStorageKey(entityId: EntityId, entityGroupId?: string): string {
  return entityGroupId ?? entityId;
}

/**
 * One form-level entity reference (layout-derived or legacy config).
 * `referenceKey` matches `FormVersion.entityReferences` map keys.
 */
export type FormEntityReferenceSlot = {
  referenceKey: string;
  entityId: EntityId;
  entityGroupId?: string;
  label: string;
};

export type EmailExtractionConfig = {
  tableId: ArTableId;
  columns: EmailExtractionColumnConfig[];
  entityReference?: EmailExtractionEntityConfig;
  fields?: FormLevelFieldDescriptors;
  extractionPrompt?: string;
  mappingSystemPrompt?: string;
  mappingInstruction?: string;
};

export type EmailRoutingRule = {
  formTypes?: FormTypeId[];
  senderDomains?: string[];
  keywords?: string[];
  assignToEmail: string;
};

export type AiEmailAnalysisParameters = DocDataWithId & {
  companyDescription: string;
  systemPrompt: string;
  prompt: string;
  receivedEmailSystemPrompt?: string;
  receivedEmailPrompt?: string;
  extractionSystemPrompt?: string;
  model?: string;
  temperature?: number;
  topP?: number;
  aiAdminEmail?: string;
  rules?: EmailAttachmentRule[];
  maxAttachmentsPerEmail?: number;
  maxTotalBytesPerEmail?: number;
  extractionConfigs?: Partial<Record<FormTypeId, EmailExtractionConfig>>;
  defaultFormType?: FormTypeId;
  routingRules?: EmailRoutingRule[];
  minimumConfidence?: number;
};

/** Default system prompt for top-level inbound email analysis (intent + routing). */
export const defaultAiEmailAnalysisSystemPrompt =
  'You are an assistant that extracts structured email information from raw emails using a structured JSON output format.';

/** Default system prompt for line-item extraction from emails and their attachments. */
export const defaultAiEmailExtractionSystemPrompt =
  'You are an assistant that extracts structured line items and metadata from emails and their attachments using a structured JSON output format.';

/** Default system prompt for quote extraction (currently identical to email-extraction prompt — kept distinct so they can diverge). */
export const defaultAiQuoteExtractionSystemPrompt =
  'You are an assistant that extracts structured line items and metadata from emails and their attachments using a structured JSON output format.';

/** Default system prompt for mapping source document columns to a target schema (file extraction path). */
export const defaultExtractionMappingSystemPrompt =
  'You are a data mapping assistant. Map source document columns to target schema fields.';

/** Default user instruction for column-mapping inference. Reused by both file and email mapping flows. */
export const defaultMappingInstruction =
  `For each source column, determine the best matching target field.
Set targetTypeId to the exact field ID from the target fields list.
Set targetTypeId to null and targetLabel to null if the column should be skipped.
Set confidence between 0 and 1 based on how well the source matches the target.`;

/** Alias of {@link defaultMappingInstruction} used by the file-extraction path. */
export const defaultExtractionMappingInstruction = defaultMappingInstruction;

/** Default system prompt for mapping email extraction columns to target table fields. */
export const defaultEmailMappingSystemPrompt =
  'You are a data mapping assistant. Map source email extraction columns to target table fields.';

/** Alias of {@link defaultMappingInstruction} used by the email-mapping path. */
export const defaultEmailMappingInstruction = defaultMappingInstruction;

/** Default sampling temperature for email-analysis runs — low to favor deterministic extraction. */
export const defaultEmailAnalysisTemperature = 0.2;

/** Default top-p for email-analysis runs. Paired with `defaultEmailAnalysisTemperature`. */
export const defaultEmailAnalysisTopP = 0.9;

/**
 * Default user prompt for top-level inbound email analysis. Templated with
 * `{{companyName}}`, `{{companyDescription}}`, `{{systemFromEmail}}`,
 * `{{actionItemAssignedTo}}`, `{{threadContext}}`, `{{emailText}}`,
 * `{{attachmentSummary}}` at render time.
 */
export const defaultAiEmailAnalysisPrompt = `Review the following email, understand the content and intent, given the context of the email and email chain / previous emails.
The previous emails are in the order they were sent and responded to. Note that the email may have responses and forwards. Be careful not to mix up the order of the emails.
Reflect on the purpose of the email and the intent of the latest sender, as well as the senders in order of the previous emails in the thread.
Extract the action item from the email. The action item is the next step that needs to be taken based on the email.
Utilize the customer company name {{companyName}} and context about the company: {{companyDescription}} to infer action items that are relevant to the customer.
The system email address is {{systemFromEmail}} - emails from this address are from the customer's system.
The default assignee for action items is {{actionItemAssignedTo}} when no specific assignee can be determined.
Typically incoming emails to customers will have action items that the customer needs to take, and are assigned typically to the customer's team, likely the latest recipient.
Respond with JSON object adhering to the schema provided.
Use defaults for any missing fields, but ensure intent and actions are filled in from your understanding of the email.
If the email is clearly not requesting any business action (e.g. spam, automated notifications, newsletters), omit the formType field from the intent object.
{{threadContext}}
Email:
{{emailText}}
{{attachmentSummary}}`;

/**
 * Default system prompt used when an inbound email reaches the analysis
 * pipeline. Aliases {@link defaultAiEmailAnalysisSystemPrompt} so received-email
 * configuration starts identical to the general analysis prompt; tenants can
 * override via `AiEmailAnalysisParameters.receivedEmailSystemPrompt`.
 */
export const defaultReceivedEmailSystemPrompt = defaultAiEmailAnalysisSystemPrompt;

/**
 * Default user prompt for received-email analysis. Aliases
 * {@link defaultAiEmailAnalysisPrompt} for the same reason as
 * {@link defaultReceivedEmailSystemPrompt}.
 */
export const defaultReceivedEmailPrompt = defaultAiEmailAnalysisPrompt;

export type PromptHistoryChangeType = 'edit' | 'reset-to-default' | 'revert';

export type PromptHistoryEntry = {
  previousValue: string;
  newValue: string;
  changedAt: Timestamp;
  changedBy: { uid: string; email: string };
  changeType: PromptHistoryChangeType;
};

/**
 * Firestore document path that stores per-prompt edit history (every change
 * to user-tunable system prompts). Append-only; consumed by the prompt
 * history UI and audit views.
 */
export const PROMPT_HISTORY_ROOT = 'parameters/promptHistory';
